// Operator Desktop (Business plane) i18n dictionary — STATIC UI CHROME ONLY.
// Split-out module merged into the flat per-language lookup in translations.js (same t() semantics:
// translations[lang][key] -> translations.en[key] -> key; {placeholders} interpolated by t()).
//
// Covers the three Operator tools: Unified Agent Desktop, Enhanced AI Voice Simulator, and the
// AI Chat Simulator (Enhanced). Keys are identical across all 10 languages (full parity).
//
// NEVER used for dynamic/backend/user content: customer/agent/session names, chat & voice transcript
// text, QA test-scenario definitions (simulated messages), canned-response bodies, counts, dates,
// detected language/intent values, or raw backend error strings. Those stay verbatim in the code.

const operatorDeskTranslations = {
  en: {
    'od.enableSound': 'Enable sound',
    // ── AI-Voice human handoff (Phase C): media state + transcript/summary ──
    'od.mediaJoining': 'Connecting you to the customer…',
    'od.waitingForCustomerOffer': 'Waiting for the customer to connect…',
    'od.mediaConnecting': 'Establishing live audio…',
    'od.humanCallActive': 'Human Call Active — live audio connected',
    'od.mediaFailed': 'Could not establish live audio. The customer has been notified.',
    'od.aiContext': 'AI context',
    'od.liveTranscript': 'Live Transcript',
    'od.callSummary': 'Call Summary',
    'od.actionItems': 'Action Items',
    'od.commitments': 'Commitments',
    'od.speakerAgent': 'Agent',
    'od.speakerCustomer': 'Customer',
    // ── Shared ──────────────────────────────────────────────────────────────
    'od.networkError': 'Network error',
    'od.missingInformation': 'Missing Information',
    'od.customer': 'Customer',
    'od.aiAgent': 'AI Agent',
    'od.escalated': 'Escalated',
    'od.sendMessage': 'Send Message',
    'od.testScenarios': 'Test Scenarios',
    'od.selectedScenario': 'Selected Scenario',
    'od.expected': 'Expected:',
    'od.conversation': 'Conversation',
    'od.sessionLabel': 'Session reference:',
    'od.languageLabel': 'Language:',
    'od.intentLabel': 'Intent:',
    'od.detecting': 'Detecting...',
    'od.testEmailOverride': 'Test Email Override',
    'od.optionalSimulatorOnly': '(optional — simulator only)',
    'od.emailOverridePlaceholder': 'e.g. you@example.com — bypasses phone lookup',
    'od.customerName': 'Customer Name',
    'od.escalatingTo': 'Escalating to {target}: {reason}',
    'od.processingFailed': 'Processing Failed',
    'od.processFailedFallback': 'Could not process your message. Please try again.',
    'od.callEnded': 'Call Ended',
    'od.tabBasic': 'Basic',
    'od.tabComplex': 'Complex',
    'od.tabAmbiguous': 'Ambiguous',
    'od.tabMultilingual': 'Multilingual',
    'od.tabEmpathy': 'Empathy',
    'od.tabEnvironment': 'Environment',
    'od.tabContextual': 'Contextual',
    // ── Unified Agent Desktop ───────────────────────────────────────────────
    'od.subtitleUnifiedDesktop': 'All channels, customer data, and tools in one interface',
    'od.takeBreak': 'Take Break',
    'od.endBreak': 'End Break',
    'od.statusUpdateFailed': 'Could not update your status. Please try again.',
    'od.channelUnavailable': 'No queue configured for this channel',
    'od.answered': 'Interaction accepted',
    'od.answeredDesc': 'You are now handling this interaction.',
    'od.answeredNoMedia': 'Accepted. No live audio session — handling by chat/record.',
    'od.noActiveCallDesc': 'There is no active interaction to end.',
    'od.actionFailed': 'Action failed',
    'od.staffMemberFallback': 'the selected operator',
    'od.noActiveAudio': 'No active audio',
    'od.noActiveAudioDesc': 'There is no live audio session to control.',
    'od.disabledNoIncoming': 'No incoming interaction to answer',
    'od.disabledNoActive': 'No active interaction',
    'od.disabledNoTransferTargets': 'No available staff to transfer to',
    'od.disabledNoAudio': 'No active audio session',
    'od.noAvailableStaff': 'No available staff to transfer to right now.',
    'od.transferNotesLabel': 'Transfer notes (optional)',
    'od.transferNotesPlaceholder': 'Context for the receiving operator…',
    'od.noReportContext': 'No interaction to report on',
    'od.noReportContextDesc': 'Answer or select an active interaction to generate a report.',
    'od.reportGenerated': 'Report generated',
    'od.reportGeneratedDesc': 'The report has been generated and saved.',
    'od.reportCopied': 'Report copied to clipboard',
    'od.reportInclude': 'Include in report',
    'od.reportInclude_transcript': 'Transcript summary',
    'od.reportInclude_ticket': 'Linked ticket',
    'od.reportInclude_callback': 'Linked callback',
    'od.reportInclude_resolution': 'Resolution status',
    'od.reportNotesLabel': 'Report notes (optional)',
    'od.reportNotesPlaceholder': 'Add any context for this report…',
    'od.reportContextFor': 'Reporting on the active interaction with {customer}.',
    'od.customerFallback': 'the customer',
    'od.reportStatusLine': 'Status: {status} · Format: {format}',
    'od.reportCopy': 'Copy',
    'od.reportDownload': 'Download',
    'od.reportGenerating': 'Generating…',
    'od.screenShareRequiresInteraction': 'No active interaction',
    'od.screenShareRequiresInteractionDesc': 'Answer or select an active interaction before requesting screen share.',
    'od.screenShareRequested': 'Screen-share request sent',
    'od.screenShareWaiting': 'Waiting for the customer to accept.',
    'od.screenShareCancelled': 'Screen-share request cancelled',
    'od.screenShareReasonLabel': 'Reason (optional)',
    'od.screenShareReasonPlaceholder': 'Why do you need to view the customer’s screen?',
    'od.screenShareHonestNote': 'A request will be sent to the customer. Screen share becomes active only after the customer accepts.',
    'od.screenShareStatusLabel': 'Status',
    'od.ssStatus_requested': 'Requested',
    'od.ssStatus_pending_customer_acceptance': 'Waiting for customer to accept',
    'od.ssStatus_accepted': 'Accepted by customer',
    'od.ssStatus_active': 'Active',
    'od.ssStatus_declined': 'Declined by customer',
    'od.ssStatus_cancelled': 'Cancelled',
    'od.ssStatus_expired': 'Expired',
    'od.ssStatus_ended': 'Ended',
    'od.ssStatus_unknown': 'Unknown',
    'od.screenShareWith': 'Screen share with {customer}',
    'od.screenShareJoin': 'Join screen share',
    'od.screenShareNoLinkYet': 'No live session yet — waiting for the customer to accept.',
    'od.screenShareCancel': 'Cancel request',
    'od.screenShareRequesting': 'Sending request…',
    'od.screenShareRequest': 'Request Screen Share',
    'od.callsToday': 'Calls Today',
    'od.avgHandleTime': 'Avg Handle Time',
    'od.target': 'Target: {value}',
    'od.firstCallResolution': 'First Call Resolution',
    'od.customerSatisfaction': 'Customer Satisfaction',
    'od.ticketsResolved': 'Tickets Resolved',
    'od.performanceExcellent': 'Performance: Excellent',
    'od.performanceUnavailable': 'Performance: —',
    'od.channelOverview': 'Channel Overview',
    'od.channelVoice': 'Voice',
    'od.channelChat': 'Chat',
    'od.channelEmail': 'Email',
    'od.channelSocial': 'Social',
    'od.callControls': 'Call Controls',
    'od.answer': 'Answer',
    'od.incomingHandoff': 'Incoming Handoff',
    'od.notEligible': 'You can’t answer this handoff',
    'od.assignedOther': 'Assigned to another operator',
    'od.noLongerAvailable': 'No longer available',
    'od.handoffExpired': 'Handoff expired',
    'od.alreadyAcceptedOther': 'Already accepted by another operator',
    'od.handoffLocked': 'This handoff isn’t actionable from your desk',
    'od.onHold': 'On hold',
    'od.live': 'Live',
    'od.connectedNoAudio': 'Connected — no live audio',
    'od.handoffChannel': '{channel} handoff',
    'od.sourceAiVoice': 'AI Voice',
    'od.sourceAiChat': 'AI Chat',
    'od.handoffToast': 'Incoming customer handoff',
    'od.handoffToastUrgent': 'Urgent customer handoff',
    'od.opCallIncoming': 'Incoming operator call',
    'od.opCallIncomingVideo': 'Incoming video call',
    'od.opCallIncomingVoice': 'Incoming voice call',
    'od.opCallCustomer': 'Customer',
    'od.opCallDecline': 'Decline',
    'od.opCallStarting': 'Starting…',
    'od.opCallAnswered': 'Call answered',
    'od.opCallConnecting': 'Connecting — waiting for caller audio',
    'od.opCallAnsweredElsewhere': 'Answered elsewhere',
    'od.opCallAnsweredElsewhereDesc': 'This call was answered on another device.',
    'od.opCallFailed': 'Could not answer',
    'od.opCallNoConnection': 'No live connection. Reconnect and try again.',
    'od.hangUp': 'Hang Up',
    'od.resume': 'Resume',
    'od.hold': 'Hold',
    'od.mute': 'Mute',
    'od.unmute': 'Unmute',
    'od.volume': 'Volume',
    'od.customerInteraction': 'Customer Interaction - {channel}',
    'od.liveCall': 'Live Call',
    'od.issueLabel': 'Issue:',
    'od.prioritySuffix': '{priority} priority',
    'od.customerHistory': 'Customer History',
    'od.resolved': 'Resolved',
    'od.pending': 'Pending',
    'od.callNotes': 'Call Notes',
    'od.addNotesPlaceholder': 'Add notes about this call...',
    'od.quickResponsePlaceholder': 'Quick response...',
    'od.handlingAs': 'Handling as {name}',
    'od.callQualityScore': 'Call Quality Score',
    'od.micMuted': 'Microphone is muted',
    'od.saveResolve': 'Save & Resolve',
    'od.escalate': 'Escalate',
    'od.transfer': 'Transfer',
    'od.readyNext': 'Ready for Next Interaction',
    'od.waitingIncoming': 'Waiting for incoming {channel} interaction',
    'od.queueWaiting': 'Queue: {count} waiting',
    'od.tabKB': 'KB',
    'od.tabQuick': 'Quick',
    'od.tabTools': 'Tools',
    'od.knowledgeBase': 'Knowledge Base',
    'od.searchSolutions': 'Search solutions...',
    'od.noApprovedSolutions': 'No approved solutions yet.',
    'od.noSolutionsMatch': 'No solutions match your search.',
    'od.clickToInsert': 'Click to insert this solution into your reply',
    'od.usesSuffix': '{count} uses',
    'od.quickResponses': 'Quick Responses',
    'od.productivityTools': 'Productivity Tools',
    'od.createTicket': 'Create Ticket',
    'od.createNewTicket': 'Create New Ticket',
    'od.createTicketDesc': 'Create a support ticket for the current customer issue',
    'od.titleRequired': 'Title *',
    'od.descriptionRequired': 'Description *',
    'od.priority': 'Priority',
    'od.category': 'Category',
    'od.customerNameEmailPlaceholder': 'Customer name or email',
    'od.briefIssuePlaceholder': 'Brief description of the issue',
    'od.detailedIssuePlaceholder': 'Detailed description of the issue',
    'od.priorityLow': 'Low',
    'od.priorityMedium': 'Medium',
    'od.priorityHigh': 'High',
    'od.priorityUrgent': 'Urgent',
    'od.catTechnical': 'Technical Support',
    'od.catBilling': 'Billing',
    'od.catProduct': 'Product Issues',
    'od.catAccount': 'Account Management',
    'od.scheduleCallback': 'Schedule Callback',
    'od.scheduleCallbackDesc': 'Schedule a callback for the customer',
    'od.customerNameRequired': 'Customer Name *',
    'od.phoneNumber': 'Phone Number',
    'od.customerNamePlaceholder': 'Customer name',
    'od.phonePlaceholder': '+1-555-0123',
    'od.dateRequired': 'Date *',
    'od.timeRequired': 'Time *',
    'od.notes': 'Notes',
    'od.callbackNotesPlaceholder': 'Additional notes for the callback',
    'od.generateReport': 'Generate Report',
    'od.generateReportDesc': 'Generate a performance or activity report',
    'od.reportType': 'Report Type',
    'od.timePeriod': 'Time Period',
    'od.format': 'Format',
    'od.reportPerformance': 'Performance Report',
    'od.reportActivity': 'Activity Report',
    'od.reportCustomer': 'Customer Interactions',
    'od.reportResolution': 'Resolution Summary',
    'od.periodToday': 'Today',
    'od.periodWeek': 'This Week',
    'od.periodMonth': 'This Month',
    'od.periodQuarter': 'This Quarter',
    'od.screenShare': 'Screen Share',
    'od.screenShareDesc': 'Start a screen sharing session with the customer',
    'od.startScreenShare': 'Start Screen Share',
    'od.screenShareInfo': 'This will allow the customer to see your screen for better assistance',
    'od.secureConnection': 'Secure connection',
    'od.endToEndEncrypted': 'End-to-end encrypted',
    'od.customerNotified': 'Customer will be notified',
    'od.transferCall': 'Transfer Call',
    'od.transferCallDesc': 'Select where to transfer the current call',
    'od.transferTo': 'Transfer To',
    'od.selectTransferDest': 'Select transfer destination',
    'od.destManager': 'Manager',
    'od.destBilling': 'Billing Department',
    'od.destSales': 'Sales Team',
    'od.destSupervisor': 'Supervisor',
    'od.adjustVolume': 'Adjust Call Volume',
    'od.adjustVolumeDesc': 'Adjust the volume level for the current call',
    'od.volumeLevel': 'Volume Level: {value}%',
    'od.saveResolveTicket': 'Save & Resolve Ticket',
    'od.saveResolveDesc': 'Add resolution notes and mark this ticket as resolved',
    'od.resolutionNotesLabel': 'Resolution Notes *',
    'od.resolutionPlaceholder': 'Describe how the issue was resolved...',
    'od.resolveInfo': 'This will mark the ticket as resolved and close the current interaction.',
    'od.escalateTicket': 'Escalate Ticket',
    'od.escalateTicketDesc': 'Escalate this ticket to a supervisor or specialist',
    'od.escalationReasonLabel': 'Escalation Reason *',
    'od.escalationPlaceholder': 'Explain why this ticket needs to be escalated...',
    'od.escalateTo': 'Escalate To',
    'od.escAdmin': 'Admin',
    'od.escalateInfo': 'The ticket will be transferred to the selected team for further assistance.',
    // Unified Agent Desktop toasts
    'od.noIncomingCall': 'No Incoming Call',
    'od.noIncomingCallDesc': 'There is no incoming call to answer.',
    'od.callOnHold': 'Call On Hold',
    'od.callOnHoldDesc': 'Microphone muted. Customer is on hold.',
    'od.callResumed': 'Call Resumed',
    'od.callResumedDesc': 'Microphone active. Customer can hear you.',
    'od.callDisconnected': 'Call has been disconnected.',
    'od.transferTargetRequired': 'Transfer Target Required',
    'od.transferTargetRequiredDesc': 'Please select a staff member to transfer to.',
    'od.noActiveCall': 'No Active Call',
    'od.noActiveCallTransfer': 'There is no active WebRTC call to transfer.',
    'od.callTransferred': 'Call Transferred',
    'od.callTransferredDesc': 'Call transferred to staff member {target}.',
    'od.transferFailed': 'Transfer Failed',
    'od.noSolutionText': 'No solution text',
    'od.noSolutionTextDesc': 'This solution has no insertable content yet.',
    'od.solutionInserted': 'Solution inserted',
    'od.solutionInsertedDesc': '"{title}" added to your reply.',
    'od.solution': 'Solution',
    'od.fillTitleDescription': 'Please fill in title and description',
    'od.ticketCreated': 'Ticket Created',
    'od.ticketCreatedDesc': 'Conversation {id} created.',
    'od.createFailed': 'Create Failed',
    'od.fillCustomerDateTime': 'Please fill in customer, date, and time',
    'od.callbackScheduled': 'Callback Scheduled',
    'od.callbackScheduledDesc': 'Callback for {customer} saved.',
    'od.callbackFailed': 'Callback Failed',
    'od.reportDownloaded': 'Report Downloaded',
    'od.reportDownloadedDesc': 'Report generated from live data.',
    'od.reportFailed': 'Report Failed',
    'od.screenShareRequiresCall': 'Screen share requires an active WebRTC call with the customer.',
    'od.screenShareActive': 'Screen Share Active',
    'od.screenShareActiveDesc': 'Your screen is now visible to the customer.',
    'od.screenShareFailed': 'Screen Share Failed',
    'od.screenShareFailedFallback': 'Could not start screen share.',
    'od.resolutionNotesRequired': 'Resolution Notes Required',
    'od.resolutionNotesRequiredDesc': 'Please provide resolution notes before saving.',
    'od.noActiveTicket': 'No Active Ticket',
    'od.selectTicketResolve': 'Select a ticket before resolving.',
    'od.ticketResolved': 'Ticket Resolved',
    'od.ticketResolvedDesc': 'Ticket {id} resolved and persisted.',
    'od.resolutionFailed': 'Resolution Failed',
    'od.escalationReasonRequired': 'Escalation Reason Required',
    'od.escalationReasonRequiredDesc': 'Please provide a reason for escalation.',
    'od.selectTicketEscalate': 'Select a ticket before escalating.',
    'od.escalationCreatedDesc': 'Escalation {id} created.',
    'od.escalationFailed': 'Escalation Failed',
    'od.noConversationLinked': 'No Conversation Linked',
    'od.noConversationLinkedDesc': 'This escalation has no linked conversation. Cannot send message.',
    'od.messageSent': 'Message Sent',
    'od.messageSentDesc': 'Customer will receive this message.',
    'od.sendFailed': 'Send Failed',
    // ── AI Voice Simulator ──────────────────────────────────────────────────
    'od.subtitleVoiceSimulator': 'Test AI voice agent with complex scenarios, multilingual queries, and emotional contexts',
    'od.crmAuthReady': 'CRM Auth Ready',
    'od.calling': 'Calling:',
    'od.selectScenarioVoice': 'Select a scenario to test AI capabilities',
    'od.selectLanguage': 'Select language',
    'od.langEnglish': 'English',
    'od.langFrench': 'French',
    'od.langMixed': 'Mixed',
    'od.initiateCall': 'Initiate Call',
    'od.endCall': 'End Call',
    'od.processing': 'Processing',
    'od.active': 'Active',
    'od.idle': 'Idle',
    'od.initiateCallToStart': 'Initiate a call to start testing',
    'od.listening': 'Listening...',
    'od.clickToSpeak': 'Click to speak one message',
    'od.turnOffVoiceMode': 'Turn off continuous voice mode',
    'od.turnOnVoiceMode': 'Turn on continuous voice mode',
    'od.aiVoice': 'AI Voice',
    'od.on': 'ON',
    'od.off': 'OFF',
    'od.thinking': 'Thinking',
    'od.voiceInputPlaceholder': 'Type a message and press Enter (Shift+Enter for newline), or use the mic buttons above...',
    'od.vsReady': 'Ready',
    'od.vsProcessing': 'Processing...',
    'od.vsSpeaking': 'Speaking...',
    'od.vsError': 'Mic Error',
    'od.notSupported': 'Not Supported',
    'od.speechRequires': 'Speech recognition requires Chrome, Edge, or Safari 14.1+.',
    'od.micBlocked': 'Microphone Blocked',
    'od.micBlockedDesc': 'Allow microphone access in your browser settings and try again.',
    'od.callInitiated': 'Call Initiated',
    'od.aiVoiceConnected': 'AI Voice Agent connected. Session: {session}',
    'od.callFailed': 'Call Failed',
    'od.callFailedDesc': 'Could not initiate AI call. Please try again.',
    'od.callEscalated': 'Call Escalated',
    'od.aiVoiceTerminated': 'AI Voice session terminated.',
    // ── AI Chat Simulator ───────────────────────────────────────────────────
    'od.chatSimSubtitle': 'QA testing simulator — sends typed messages directly to the live AI chat backend. Not a real customer session. Test AI chat agent with 30+ scenarios including complex situations, multilingual queries, and emotional contexts.',
    'od.qaSimulatorMode': 'QA Simulator Mode',
    'od.userLabel': 'User:',
    'od.guest': 'Guest',
    'od.crmAuth': 'CRM Auth',
    'od.restart': 'Restart',
    'od.connected': 'Connected',
    'od.aiActive': 'AI Active',
    'od.realTime': 'Real-time',
    'od.voiceReady': 'Voice Ready',
    'od.listeningShort': 'Listening',
    'od.chattingWith': 'Chatting with:',
    'od.selectScenarioChat': 'Select a scenario to test AI chat capabilities',
    'od.chatControls': 'Chat Controls',
    'od.customerPhone': 'Customer Phone',
    'od.startChat': 'Start Chat',
    'od.endChat': 'End Chat',
    'od.startChatToTest': 'Start a chat session to begin testing',
    'od.chatInputPlaceholder': 'Type your message or select a scenario...',
    'od.clear': 'Clear',
    'od.chatInitiated': 'Chat Initiated',
    'od.aiChatConnected': 'AI Chat Agent connected. Session: {session}',
    'od.chatFailed': 'Chat Failed',
    'od.chatFailedDesc': 'Could not initiate AI chat. Please try again.',
    'od.chatEscalated': 'Chat Escalated',
    'od.chatEnded': 'Chat Ended',
    'od.aiChatTerminated': 'AI Chat session terminated.',
    // ── Customer communication quick-tools (Call-back / Message / Email active customer) ─────
    'od.customerCommunication': 'Customer Communication',
    'od.callCustomer': 'Call Back Customer',
    'od.messageCustomer': 'Message Customer',
    'od.emailCustomer': 'Email Customer',
    'od.noActiveInteraction': 'No Active Interaction',
    'od.acceptInteractionFirst': 'Accept a customer interaction first.',
    'od.customerPhoneUnavailable': 'No Customer Phone',
    'od.customerPhoneUnavailableDesc': 'No phone number is on file for this customer. Update the customer record to enable a callback.',
    'od.customerEmailUnavailable': 'No Customer Email',
    'od.customerEmailUnavailableDesc': 'No email address is on file for this customer. Update the customer record to enable email.',
    'od.quickMessage': 'Quick Message',
    'od.quickMessageDesc': "Send a message into the customer's conversation thread.",
    'od.quickMessagePlaceholder': 'Type a message to the customer...',
    'od.emptyMessage': 'Empty Message',
    'od.emptyMessageDesc': 'Please enter a message to send.',
    'od.sending': 'Sending...',
    'od.emailCustomerDesc': 'Send an email to the active customer.',
    'od.emailToLabel': 'To',
    'od.emailSubjectLabel': 'Subject',
    'od.emailSubjectPlaceholder': 'Subject',
    'od.emailBodyLabel': 'Message',
    'od.emailBodyPlaceholder': 'Write your email...',
    'od.emailFillSubjectBody': 'Please fill in subject and message body.',
    'od.missingFields': 'Missing Fields',
    'od.sendEmail': 'Send Email',
    'od.emailSent': 'Email Sent',
    'od.emailSentDesc': 'Email sent to {email}.',
    'od.emailSendFailed': 'Email Failed',
  },
  es: {
    'od.enableSound': 'Activar sonido',
    // ── AI-Voice human handoff (Phase C): media state + transcript/summary ──
    'od.mediaJoining': 'Conectándote con el cliente…',
    'od.waitingForCustomerOffer': 'Esperando a que el cliente se conecte…',
    'od.mediaConnecting': 'Estableciendo audio en vivo…',
    'od.humanCallActive': 'Llamada humana activa — audio en vivo conectado',
    'od.mediaFailed': 'No se pudo establecer el audio en vivo. Se notificó al cliente.',
    'od.aiContext': 'Contexto de IA',
    'od.liveTranscript': 'Transcripción en vivo',
    'od.callSummary': 'Resumen de la llamada',
    'od.actionItems': 'Acciones a realizar',
    'od.commitments': 'Compromisos',
    'od.speakerAgent': 'Agente',
    'od.speakerCustomer': 'Cliente',
    'od.networkError': 'Error de red',
    'od.missingInformation': 'Información faltante',
    'od.customer': 'Cliente',
    'od.aiAgent': 'Agente IA',
    'od.escalated': 'Escalado',
    'od.sendMessage': 'Enviar mensaje',
    'od.testScenarios': 'Escenarios de prueba',
    'od.selectedScenario': 'Escenario seleccionado',
    'od.expected': 'Esperado:',
    'od.conversation': 'Conversación',
    'od.sessionLabel': 'Referencia de sesión:',
    'od.languageLabel': 'Idioma:',
    'od.intentLabel': 'Intención:',
    'od.detecting': 'Detectando...',
    'od.testEmailOverride': 'Correo de prueba (anulación)',
    'od.optionalSimulatorOnly': '(opcional — solo simulador)',
    'od.emailOverridePlaceholder': 'p. ej. tu@ejemplo.com — omite la búsqueda por teléfono',
    'od.customerName': 'Nombre del cliente',
    'od.escalatingTo': 'Escalando a {target}: {reason}',
    'od.processingFailed': 'Error de procesamiento',
    'od.processFailedFallback': 'No se pudo procesar su mensaje. Inténtelo de nuevo.',
    'od.callEnded': 'Llamada finalizada',
    'od.tabBasic': 'Básico',
    'od.tabComplex': 'Complejo',
    'od.tabAmbiguous': 'Ambiguo',
    'od.tabMultilingual': 'Multilingüe',
    'od.tabEmpathy': 'Empatía',
    'od.tabEnvironment': 'Entorno',
    'od.tabContextual': 'Contextual',
    'od.subtitleUnifiedDesktop': 'Todos los canales, datos de clientes y herramientas en una sola interfaz',
    'od.takeBreak': 'Tomar descanso',
    'od.endBreak': 'Finalizar descanso',
    'od.callsToday': 'Llamadas hoy',
    'od.avgHandleTime': 'Tiempo medio de gestión',
    'od.target': 'Objetivo: {value}',
    'od.firstCallResolution': 'Resolución en primera llamada',
    'od.customerSatisfaction': 'Satisfacción del cliente',
    'od.ticketsResolved': 'Tickets resueltos',
    'od.performanceExcellent': 'Rendimiento: Excelente',
    'od.channelOverview': 'Resumen de canales',
    'od.channelVoice': 'Voz',
    'od.channelChat': 'Chat',
    'od.channelEmail': 'Correo',
    'od.channelSocial': 'Social',
    'od.callControls': 'Controles de llamada',
    'od.answer': 'Responder',
    'od.incomingHandoff': 'Transferencia entrante',
    'od.notEligible': 'No puede atender esta transferencia',
    'od.assignedOther': 'Asignada a otro operador',
    'od.noLongerAvailable': 'Ya no está disponible',
    'od.handoffExpired': 'La transferencia expiró',
    'od.alreadyAcceptedOther': 'Ya aceptada por otro operador',
    'od.handoffLocked': 'Esta transferencia no se puede gestionar desde su puesto',
    'od.onHold': 'En espera',
    'od.live': 'En vivo',
    'od.connectedNoAudio': 'Conectado — sin audio en vivo',
    'od.handoffChannel': 'Transferencia de {channel}',
    'od.sourceAiVoice': 'Voz IA',
    'od.sourceAiChat': 'Chat IA',
    'od.handoffToast': 'Transferencia de cliente entrante',
    'od.handoffToastUrgent': 'Transferencia de cliente urgente',
    'od.opCallIncoming': 'Llamada de operador entrante',
    'od.opCallIncomingVideo': 'Videollamada entrante',
    'od.opCallIncomingVoice': 'Llamada de voz entrante',
    'od.opCallCustomer': 'Cliente',
    'od.opCallDecline': 'Rechazar',
    'od.opCallStarting': 'Iniciando…',
    'od.opCallAnswered': 'Llamada atendida',
    'od.opCallConnecting': 'Conectando: esperando el audio del cliente',
    'od.opCallAnsweredElsewhere': 'Atendida en otro dispositivo',
    'od.opCallAnsweredElsewhereDesc': 'Esta llamada se atendió en otro dispositivo.',
    'od.opCallFailed': 'No se pudo responder',
    'od.opCallNoConnection': 'Sin conexion activa. Vuelve a conectarte e intentalo de nuevo.',
    'od.hangUp': 'Colgar',
    'od.resume': 'Reanudar',
    'od.hold': 'En espera',
    'od.mute': 'Silenciar',
    'od.unmute': 'Activar micrófono',
    'od.volume': 'Volumen',
    'od.customerInteraction': 'Interacción con el cliente - {channel}',
    'od.liveCall': 'Llamada en vivo',
    'od.issueLabel': 'Problema:',
    'od.prioritySuffix': 'prioridad {priority}',
    'od.customerHistory': 'Historial del cliente',
    'od.resolved': 'Resuelto',
    'od.pending': 'Pendiente',
    'od.callNotes': 'Notas de la llamada',
    'od.addNotesPlaceholder': 'Añada notas sobre esta llamada...',
    'od.quickResponsePlaceholder': 'Respuesta rápida...',
    'od.handlingAs': 'Atendiendo como {name}',
    'od.callQualityScore': 'Puntuación de calidad de llamada',
    'od.micMuted': 'El micrófono está silenciado',
    'od.saveResolve': 'Guardar y resolver',
    'od.escalate': 'Escalar',
    'od.transfer': 'Transferir',
    'od.readyNext': 'Listo para la próxima interacción',
    'od.waitingIncoming': 'Esperando interacción entrante de {channel}',
    'od.queueWaiting': 'Cola: {count} en espera',
    'od.tabKB': 'BC',
    'od.tabQuick': 'Rápidas',
    'od.tabTools': 'Herramientas',
    'od.knowledgeBase': 'Base de conocimiento',
    'od.searchSolutions': 'Buscar soluciones...',
    'od.noApprovedSolutions': 'Aún no hay soluciones aprobadas.',
    'od.noSolutionsMatch': 'Ninguna solución coincide con su búsqueda.',
    'od.clickToInsert': 'Haga clic para insertar esta solución en su respuesta',
    'od.usesSuffix': '{count} usos',
    'od.quickResponses': 'Respuestas rápidas',
    'od.productivityTools': 'Herramientas de productividad',
    'od.createTicket': 'Crear ticket',
    'od.createNewTicket': 'Crear nuevo ticket',
    'od.createTicketDesc': 'Cree un ticket de soporte para el problema actual del cliente',
    'od.titleRequired': 'Título *',
    'od.descriptionRequired': 'Descripción *',
    'od.priority': 'Prioridad',
    'od.category': 'Categoría',
    'od.customerNameEmailPlaceholder': 'Nombre o correo del cliente',
    'od.briefIssuePlaceholder': 'Breve descripción del problema',
    'od.detailedIssuePlaceholder': 'Descripción detallada del problema',
    'od.priorityLow': 'Baja',
    'od.priorityMedium': 'Media',
    'od.priorityHigh': 'Alta',
    'od.priorityUrgent': 'Urgente',
    'od.catTechnical': 'Soporte técnico',
    'od.catBilling': 'Facturación',
    'od.catProduct': 'Problemas de producto',
    'od.catAccount': 'Gestión de cuenta',
    'od.scheduleCallback': 'Programar devolución de llamada',
    'od.scheduleCallbackDesc': 'Programe una devolución de llamada para el cliente',
    'od.customerNameRequired': 'Nombre del cliente *',
    'od.phoneNumber': 'Número de teléfono',
    'od.customerNamePlaceholder': 'Nombre del cliente',
    'od.phonePlaceholder': '+1-555-0123',
    'od.dateRequired': 'Fecha *',
    'od.timeRequired': 'Hora *',
    'od.notes': 'Notas',
    'od.callbackNotesPlaceholder': 'Notas adicionales para la devolución de llamada',
    'od.generateReport': 'Generar informe',
    'od.generateReportDesc': 'Genere un informe de rendimiento o actividad',
    'od.reportType': 'Tipo de informe',
    'od.timePeriod': 'Período',
    'od.format': 'Formato',
    'od.reportPerformance': 'Informe de rendimiento',
    'od.reportActivity': 'Informe de actividad',
    'od.reportCustomer': 'Interacciones con clientes',
    'od.reportResolution': 'Resumen de resoluciones',
    'od.periodToday': 'Hoy',
    'od.periodWeek': 'Esta semana',
    'od.periodMonth': 'Este mes',
    'od.periodQuarter': 'Este trimestre',
    'od.screenShare': 'Compartir pantalla',
    'od.screenShareDesc': 'Inicie una sesión para compartir pantalla con el cliente',
    'od.startScreenShare': 'Iniciar compartir pantalla',
    'od.screenShareInfo': 'Esto permitirá que el cliente vea su pantalla para una mejor asistencia',
    'od.secureConnection': 'Conexión segura',
    'od.endToEndEncrypted': 'Cifrado de extremo a extremo',
    'od.customerNotified': 'Se notificará al cliente',
    'od.transferCall': 'Transferir llamada',
    'od.transferCallDesc': 'Seleccione a dónde transferir la llamada actual',
    'od.transferTo': 'Transferir a',
    'od.selectTransferDest': 'Seleccione el destino de la transferencia',
    'od.destManager': 'Gerente',
    'od.destBilling': 'Departamento de facturación',
    'od.destSales': 'Equipo de ventas',
    'od.destSupervisor': 'Supervisor',
    'od.adjustVolume': 'Ajustar volumen de llamada',
    'od.adjustVolumeDesc': 'Ajuste el nivel de volumen de la llamada actual',
    'od.volumeLevel': 'Nivel de volumen: {value}%',
    'od.saveResolveTicket': 'Guardar y resolver ticket',
    'od.saveResolveDesc': 'Añada notas de resolución y marque este ticket como resuelto',
    'od.resolutionNotesLabel': 'Notas de resolución *',
    'od.resolutionPlaceholder': 'Describa cómo se resolvió el problema...',
    'od.resolveInfo': 'Esto marcará el ticket como resuelto y cerrará la interacción actual.',
    'od.escalateTicket': 'Escalar ticket',
    'od.escalateTicketDesc': 'Escale este ticket a un supervisor o especialista',
    'od.escalationReasonLabel': 'Motivo de escalación *',
    'od.escalationPlaceholder': 'Explique por qué este ticket necesita ser escalado...',
    'od.escalateTo': 'Escalar a',
    'od.escAdmin': 'Administrador',
    'od.escalateInfo': 'El ticket será transferido al equipo seleccionado para más asistencia.',
    'od.noIncomingCall': 'Sin llamada entrante',
    'od.noIncomingCallDesc': 'No hay ninguna llamada entrante para responder.',
    'od.callOnHold': 'Llamada en espera',
    'od.callOnHoldDesc': 'Micrófono silenciado. El cliente está en espera.',
    'od.callResumed': 'Llamada reanudada',
    'od.callResumedDesc': 'Micrófono activo. El cliente puede oírle.',
    'od.callDisconnected': 'La llamada se ha desconectado.',
    'od.transferTargetRequired': 'Destino de transferencia requerido',
    'od.transferTargetRequiredDesc': 'Seleccione un miembro del personal al que transferir.',
    'od.noActiveCall': 'Sin llamada activa',
    'od.noActiveCallTransfer': 'No hay ninguna llamada WebRTC activa para transferir.',
    'od.callTransferred': 'Llamada transferida',
    'od.callTransferredDesc': 'Llamada transferida al miembro del personal {target}.',
    'od.transferFailed': 'Transferencia fallida',
    'od.noSolutionText': 'Sin texto de solución',
    'od.noSolutionTextDesc': 'Esta solución aún no tiene contenido insertable.',
    'od.solutionInserted': 'Solución insertada',
    'od.solutionInsertedDesc': '"{title}" añadida a su respuesta.',
    'od.solution': 'Solución',
    'od.fillTitleDescription': 'Complete el título y la descripción',
    'od.ticketCreated': 'Ticket creado',
    'od.ticketCreatedDesc': 'Conversación {id} creada.',
    'od.createFailed': 'Creación fallida',
    'od.fillCustomerDateTime': 'Complete el cliente, la fecha y la hora',
    'od.callbackScheduled': 'Devolución de llamada programada',
    'od.callbackScheduledDesc': 'Devolución de llamada para {customer} guardada.',
    'od.callbackFailed': 'Devolución de llamada fallida',
    'od.reportDownloaded': 'Informe descargado',
    'od.reportDownloadedDesc': 'Informe generado a partir de datos en vivo.',
    'od.reportFailed': 'Informe fallido',
    'od.screenShareRequiresCall': 'Compartir pantalla requiere una llamada WebRTC activa con el cliente.',
    'od.screenShareActive': 'Compartir pantalla activo',
    'od.screenShareActiveDesc': 'Su pantalla ahora es visible para el cliente.',
    'od.screenShareFailed': 'Error al compartir pantalla',
    'od.screenShareFailedFallback': 'No se pudo iniciar la pantalla compartida.',
    'od.resolutionNotesRequired': 'Notas de resolución requeridas',
    'od.resolutionNotesRequiredDesc': 'Proporcione notas de resolución antes de guardar.',
    'od.noActiveTicket': 'Sin ticket activo',
    'od.selectTicketResolve': 'Seleccione un ticket antes de resolver.',
    'od.ticketResolved': 'Ticket resuelto',
    'od.ticketResolvedDesc': 'Ticket {id} resuelto y guardado.',
    'od.resolutionFailed': 'Resolución fallida',
    'od.escalationReasonRequired': 'Motivo de escalación requerido',
    'od.escalationReasonRequiredDesc': 'Proporcione un motivo para la escalación.',
    'od.selectTicketEscalate': 'Seleccione un ticket antes de escalar.',
    'od.escalationCreatedDesc': 'Escalación {id} creada.',
    'od.escalationFailed': 'Escalación fallida',
    'od.noConversationLinked': 'Sin conversación vinculada',
    'od.noConversationLinkedDesc': 'Esta escalación no tiene conversación vinculada. No se puede enviar el mensaje.',
    'od.messageSent': 'Mensaje enviado',
    'od.messageSentDesc': 'El cliente recibirá este mensaje.',
    'od.sendFailed': 'Envío fallido',
    'od.subtitleVoiceSimulator': 'Pruebe el agente de voz IA con escenarios complejos, consultas multilingües y contextos emocionales',
    'od.crmAuthReady': 'Autenticación CRM lista',
    'od.calling': 'Llamando:',
    'od.selectScenarioVoice': 'Seleccione un escenario para probar las capacidades de la IA',
    'od.selectLanguage': 'Seleccione idioma',
    'od.langEnglish': 'Inglés',
    'od.langFrench': 'Francés',
    'od.langMixed': 'Mixto',
    'od.initiateCall': 'Iniciar llamada',
    'od.endCall': 'Finalizar llamada',
    'od.processing': 'Procesando',
    'od.active': 'Activo',
    'od.idle': 'Inactivo',
    'od.initiateCallToStart': 'Inicie una llamada para comenzar las pruebas',
    'od.listening': 'Escuchando...',
    'od.clickToSpeak': 'Haga clic para hablar un mensaje',
    'od.turnOffVoiceMode': 'Desactivar el modo de voz continuo',
    'od.turnOnVoiceMode': 'Activar el modo de voz continuo',
    'od.aiVoice': 'Voz IA',
    'od.on': 'ENC.',
    'od.off': 'APAG.',
    'od.thinking': 'Pensando',
    'od.voiceInputPlaceholder': 'Escriba un mensaje y pulse Intro (Mayús+Intro para salto de línea), o use los botones de micrófono de arriba...',
    'od.vsReady': 'Listo',
    'od.vsProcessing': 'Procesando...',
    'od.vsSpeaking': 'Hablando...',
    'od.vsError': 'Error de micrófono',
    'od.notSupported': 'No compatible',
    'od.speechRequires': 'El reconocimiento de voz requiere Chrome, Edge o Safari 14.1+.',
    'od.micBlocked': 'Micrófono bloqueado',
    'od.micBlockedDesc': 'Permita el acceso al micrófono en la configuración de su navegador e inténtelo de nuevo.',
    'od.callInitiated': 'Llamada iniciada',
    'od.aiVoiceConnected': 'Agente de voz IA conectado. Sesión: {session}',
    'od.callFailed': 'Llamada fallida',
    'od.callFailedDesc': 'No se pudo iniciar la llamada IA. Inténtelo de nuevo.',
    'od.callEscalated': 'Llamada escalada',
    'od.aiVoiceTerminated': 'Sesión de voz IA finalizada.',
    'od.chatSimSubtitle': 'Simulador de pruebas de QA: envía mensajes escritos directamente al backend de chat IA en vivo. No es una sesión real de cliente. Pruebe el agente de chat IA con más de 30 escenarios que incluyen situaciones complejas, consultas multilingües y contextos emocionales.',
    'od.qaSimulatorMode': 'Modo simulador de QA',
    'od.userLabel': 'Usuario:',
    'od.guest': 'Invitado',
    'od.crmAuth': 'Autenticación CRM',
    'od.restart': 'Reiniciar',
    'od.connected': 'Conectado',
    'od.aiActive': 'IA activa',
    'od.realTime': 'Tiempo real',
    'od.voiceReady': 'Voz lista',
    'od.listeningShort': 'Escuchando',
    'od.chattingWith': 'Chateando con:',
    'od.selectScenarioChat': 'Seleccione un escenario para probar las capacidades del chat IA',
    'od.chatControls': 'Controles de chat',
    'od.customerPhone': 'Teléfono del cliente',
    'od.startChat': 'Iniciar chat',
    'od.endChat': 'Finalizar chat',
    'od.startChatToTest': 'Inicie una sesión de chat para comenzar las pruebas',
    'od.chatInputPlaceholder': 'Escriba su mensaje o seleccione un escenario...',
    'od.clear': 'Borrar',
    'od.chatInitiated': 'Chat iniciado',
    'od.aiChatConnected': 'Agente de chat IA conectado. Sesión: {session}',
    'od.chatFailed': 'Chat fallido',
    'od.chatFailedDesc': 'No se pudo iniciar el chat IA. Inténtelo de nuevo.',
    'od.chatEscalated': 'Chat escalado',
    'od.chatEnded': 'Chat finalizado',
    'od.aiChatTerminated': 'Sesión de chat IA finalizada.',
  },
  fr: {
    'od.enableSound': 'Activer le son',
    // ── AI-Voice human handoff (Phase C): media state + transcript/summary ──
    'od.mediaJoining': 'Connexion avec le client…',
    'od.waitingForCustomerOffer': 'En attente de la connexion du client…',
    'od.mediaConnecting': 'Établissement de l\'audio en direct…',
    'od.humanCallActive': 'Appel humain actif — audio en direct connecté',
    'od.mediaFailed': 'Impossible d\'établir l\'audio en direct. Le client a été notifié.',
    'od.aiContext': 'Contexte IA',
    'od.liveTranscript': 'Transcription en direct',
    'od.callSummary': 'Résumé de l\'appel',
    'od.actionItems': 'Actions à réaliser',
    'od.commitments': 'Engagements',
    'od.speakerAgent': 'Agent',
    'od.speakerCustomer': 'Client',
    'od.networkError': 'Erreur réseau',
    'od.missingInformation': 'Informations manquantes',
    'od.customer': 'Client',
    'od.aiAgent': 'Agent IA',
    'od.escalated': 'Escaladé',
    'od.sendMessage': 'Envoyer le message',
    'od.testScenarios': 'Scénarios de test',
    'od.selectedScenario': 'Scénario sélectionné',
    'od.expected': 'Attendu :',
    'od.conversation': 'Conversation',
    'od.sessionLabel': 'Référence de session :',
    'od.languageLabel': 'Langue :',
    'od.intentLabel': 'Intention :',
    'od.detecting': 'Détection...',
    'od.testEmailOverride': 'E-mail de test (remplacement)',
    'od.optionalSimulatorOnly': '(facultatif — simulateur uniquement)',
    'od.emailOverridePlaceholder': 'ex. vous@exemple.com — contourne la recherche par téléphone',
    'od.customerName': 'Nom du client',
    'od.escalatingTo': 'Escalade vers {target} : {reason}',
    'od.processingFailed': 'Échec du traitement',
    'od.processFailedFallback': 'Impossible de traiter votre message. Veuillez réessayer.',
    'od.callEnded': 'Appel terminé',
    'od.tabBasic': 'Basique',
    'od.tabComplex': 'Complexe',
    'od.tabAmbiguous': 'Ambigu',
    'od.tabMultilingual': 'Multilingue',
    'od.tabEmpathy': 'Empathie',
    'od.tabEnvironment': 'Environnement',
    'od.tabContextual': 'Contextuel',
    'od.subtitleUnifiedDesktop': 'Tous les canaux, données clients et outils dans une seule interface',
    'od.takeBreak': 'Faire une pause',
    'od.endBreak': 'Terminer la pause',
    'od.callsToday': "Appels aujourd'hui",
    'od.avgHandleTime': 'Temps de traitement moyen',
    'od.target': 'Objectif : {value}',
    'od.firstCallResolution': 'Résolution au premier appel',
    'od.customerSatisfaction': 'Satisfaction client',
    'od.ticketsResolved': 'Tickets résolus',
    'od.performanceExcellent': 'Performance : Excellente',
    'od.channelOverview': 'Aperçu des canaux',
    'od.channelVoice': 'Voix',
    'od.channelChat': 'Chat',
    'od.channelEmail': 'E-mail',
    'od.channelSocial': 'Réseaux sociaux',
    'od.callControls': "Commandes d'appel",
    'od.answer': 'Répondre',
    'od.incomingHandoff': 'Transfert entrant',
    'od.notEligible': 'Vous ne pouvez pas prendre ce transfert',
    'od.assignedOther': 'Attribué à un autre opérateur',
    'od.noLongerAvailable': 'Plus disponible',
    'od.handoffExpired': 'Transfert expiré',
    'od.alreadyAcceptedOther': 'Déjà accepté par un autre opérateur',
    'od.handoffLocked': 'Ce transfert n’est pas actionnable depuis votre poste',
    'od.onHold': 'En attente',
    'od.live': 'En direct',
    'od.connectedNoAudio': 'Connecté — sans audio en direct',
    'od.handoffChannel': 'Transfert {channel}',
    'od.sourceAiVoice': 'Voix IA',
    'od.sourceAiChat': 'Chat IA',
    'od.handoffToast': 'Transfert client entrant',
    'od.handoffToastUrgent': 'Transfert client urgent',
    'od.opCallIncoming': 'Appel opérateur entrant',
    'od.opCallIncomingVideo': 'Appel vidéo entrant',
    'od.opCallIncomingVoice': 'Appel vocal entrant',
    'od.opCallCustomer': 'Client',
    'od.opCallDecline': 'Refuser',
    'od.opCallStarting': 'Démarrage…',
    'od.opCallAnswered': 'Appel pris',
    'od.opCallConnecting': 'Connexion — en attente de l’audio de l’appelant',
    'od.opCallAnsweredElsewhere': 'Pris sur un autre appareil',
    'od.opCallAnsweredElsewhereDesc': 'Cet appel a été pris sur un autre appareil.',
    'od.opCallFailed': 'Impossible de repondre',
    'od.opCallNoConnection': 'Aucune connexion active. Reconnectez-vous et reessayez.',
    'od.hangUp': 'Raccrocher',
    'od.resume': 'Reprendre',
    'od.hold': 'En attente',
    'od.mute': 'Couper le micro',
    'od.unmute': 'Activer le micro',
    'od.volume': 'Volume',
    'od.customerInteraction': 'Interaction client - {channel}',
    'od.liveCall': 'Appel en direct',
    'od.issueLabel': 'Problème :',
    'od.prioritySuffix': 'priorité {priority}',
    'od.customerHistory': 'Historique du client',
    'od.resolved': 'Résolu',
    'od.pending': 'En attente',
    'od.callNotes': "Notes d'appel",
    'od.addNotesPlaceholder': 'Ajoutez des notes sur cet appel...',
    'od.quickResponsePlaceholder': 'Réponse rapide...',
    'od.handlingAs': 'Traité en tant que {name}',
    'od.callQualityScore': "Score de qualité d'appel",
    'od.micMuted': 'Le microphone est coupé',
    'od.saveResolve': 'Enregistrer et résoudre',
    'od.escalate': 'Escalader',
    'od.transfer': 'Transférer',
    'od.readyNext': 'Prêt pour la prochaine interaction',
    'od.waitingIncoming': 'En attente d\'une interaction {channel} entrante',
    'od.queueWaiting': "File d'attente : {count} en attente",
    'od.tabKB': 'BC',
    'od.tabQuick': 'Rapides',
    'od.tabTools': 'Outils',
    'od.knowledgeBase': 'Base de connaissances',
    'od.searchSolutions': 'Rechercher des solutions...',
    'od.noApprovedSolutions': 'Aucune solution approuvée pour le moment.',
    'od.noSolutionsMatch': 'Aucune solution ne correspond à votre recherche.',
    'od.clickToInsert': 'Cliquez pour insérer cette solution dans votre réponse',
    'od.usesSuffix': '{count} utilisations',
    'od.quickResponses': 'Réponses rapides',
    'od.productivityTools': 'Outils de productivité',
    'od.createTicket': 'Créer un ticket',
    'od.createNewTicket': 'Créer un nouveau ticket',
    'od.createTicketDesc': 'Créez un ticket de support pour le problème actuel du client',
    'od.titleRequired': 'Titre *',
    'od.descriptionRequired': 'Description *',
    'od.priority': 'Priorité',
    'od.category': 'Catégorie',
    'od.customerNameEmailPlaceholder': 'Nom ou e-mail du client',
    'od.briefIssuePlaceholder': 'Brève description du problème',
    'od.detailedIssuePlaceholder': 'Description détaillée du problème',
    'od.priorityLow': 'Faible',
    'od.priorityMedium': 'Moyenne',
    'od.priorityHigh': 'Élevée',
    'od.priorityUrgent': 'Urgente',
    'od.catTechnical': 'Support technique',
    'od.catBilling': 'Facturation',
    'od.catProduct': 'Problèmes de produit',
    'od.catAccount': 'Gestion de compte',
    'od.scheduleCallback': 'Planifier un rappel',
    'od.scheduleCallbackDesc': 'Planifiez un rappel pour le client',
    'od.customerNameRequired': 'Nom du client *',
    'od.phoneNumber': 'Numéro de téléphone',
    'od.customerNamePlaceholder': 'Nom du client',
    'od.phonePlaceholder': '+1-555-0123',
    'od.dateRequired': 'Date *',
    'od.timeRequired': 'Heure *',
    'od.notes': 'Notes',
    'od.callbackNotesPlaceholder': 'Notes supplémentaires pour le rappel',
    'od.generateReport': 'Générer un rapport',
    'od.generateReportDesc': "Générez un rapport de performance ou d'activité",
    'od.reportType': 'Type de rapport',
    'od.timePeriod': 'Période',
    'od.format': 'Format',
    'od.reportPerformance': 'Rapport de performance',
    'od.reportActivity': "Rapport d'activité",
    'od.reportCustomer': 'Interactions clients',
    'od.reportResolution': 'Résumé des résolutions',
    'od.periodToday': "Aujourd'hui",
    'od.periodWeek': 'Cette semaine',
    'od.periodMonth': 'Ce mois-ci',
    'od.periodQuarter': 'Ce trimestre',
    'od.screenShare': "Partage d'écran",
    'od.screenShareDesc': "Démarrez une session de partage d'écran avec le client",
    'od.startScreenShare': "Démarrer le partage d'écran",
    'od.screenShareInfo': 'Cela permettra au client de voir votre écran pour une meilleure assistance',
    'od.secureConnection': 'Connexion sécurisée',
    'od.endToEndEncrypted': 'Chiffré de bout en bout',
    'od.customerNotified': 'Le client sera notifié',
    'od.transferCall': "Transférer l'appel",
    'od.transferCallDesc': "Sélectionnez où transférer l'appel actuel",
    'od.transferTo': 'Transférer à',
    'od.selectTransferDest': 'Sélectionnez la destination du transfert',
    'od.destManager': 'Gestionnaire',
    'od.destBilling': 'Service de facturation',
    'od.destSales': 'Équipe commerciale',
    'od.destSupervisor': 'Superviseur',
    'od.adjustVolume': "Ajuster le volume d'appel",
    'od.adjustVolumeDesc': "Ajustez le niveau de volume de l'appel actuel",
    'od.volumeLevel': 'Niveau de volume : {value} %',
    'od.saveResolveTicket': 'Enregistrer et résoudre le ticket',
    'od.saveResolveDesc': 'Ajoutez des notes de résolution et marquez ce ticket comme résolu',
    'od.resolutionNotesLabel': 'Notes de résolution *',
    'od.resolutionPlaceholder': 'Décrivez comment le problème a été résolu...',
    'od.resolveInfo': "Cela marquera le ticket comme résolu et fermera l'interaction actuelle.",
    'od.escalateTicket': 'Escalader le ticket',
    'od.escalateTicketDesc': 'Escaladez ce ticket vers un superviseur ou un spécialiste',
    'od.escalationReasonLabel': "Motif d'escalade *",
    'od.escalationPlaceholder': 'Expliquez pourquoi ce ticket doit être escaladé...',
    'od.escalateTo': 'Escalader vers',
    'od.escAdmin': 'Administrateur',
    'od.escalateInfo': "Le ticket sera transféré à l'équipe sélectionnée pour une assistance supplémentaire.",
    'od.noIncomingCall': "Aucun appel entrant",
    'od.noIncomingCallDesc': "Il n'y a aucun appel entrant à prendre.",
    'od.callOnHold': 'Appel en attente',
    'od.callOnHoldDesc': 'Microphone coupé. Le client est en attente.',
    'od.callResumed': 'Appel repris',
    'od.callResumedDesc': 'Microphone actif. Le client peut vous entendre.',
    'od.callDisconnected': "L'appel a été déconnecté.",
    'od.transferTargetRequired': 'Destination de transfert requise',
    'od.transferTargetRequiredDesc': 'Veuillez sélectionner un membre du personnel vers qui transférer.',
    'od.noActiveCall': 'Aucun appel actif',
    'od.noActiveCallTransfer': "Il n'y a aucun appel WebRTC actif à transférer.",
    'od.callTransferred': 'Appel transféré',
    'od.callTransferredDesc': 'Appel transféré au membre du personnel {target}.',
    'od.transferFailed': 'Échec du transfert',
    'od.noSolutionText': 'Aucun texte de solution',
    'od.noSolutionTextDesc': "Cette solution n'a pas encore de contenu insérable.",
    'od.solutionInserted': 'Solution insérée',
    'od.solutionInsertedDesc': '« {title} » ajoutée à votre réponse.',
    'od.solution': 'Solution',
    'od.fillTitleDescription': 'Veuillez remplir le titre et la description',
    'od.ticketCreated': 'Ticket créé',
    'od.ticketCreatedDesc': 'Conversation {id} créée.',
    'od.createFailed': 'Échec de la création',
    'od.fillCustomerDateTime': "Veuillez remplir le client, la date et l'heure",
    'od.callbackScheduled': 'Rappel planifié',
    'od.callbackScheduledDesc': 'Rappel pour {customer} enregistré.',
    'od.callbackFailed': 'Échec du rappel',
    'od.reportDownloaded': 'Rapport téléchargé',
    'od.reportDownloadedDesc': 'Rapport généré à partir de données en direct.',
    'od.reportFailed': 'Échec du rapport',
    'od.screenShareRequiresCall': "Le partage d'écran nécessite un appel WebRTC actif avec le client.",
    'od.screenShareActive': "Partage d'écran actif",
    'od.screenShareActiveDesc': 'Votre écran est désormais visible par le client.',
    'od.screenShareFailed': "Échec du partage d'écran",
    'od.screenShareFailedFallback': "Impossible de démarrer le partage d'écran.",
    'od.resolutionNotesRequired': 'Notes de résolution requises',
    'od.resolutionNotesRequiredDesc': "Veuillez fournir des notes de résolution avant d'enregistrer.",
    'od.noActiveTicket': 'Aucun ticket actif',
    'od.selectTicketResolve': 'Sélectionnez un ticket avant de résoudre.',
    'od.ticketResolved': 'Ticket résolu',
    'od.ticketResolvedDesc': 'Ticket {id} résolu et enregistré.',
    'od.resolutionFailed': 'Échec de la résolution',
    'od.escalationReasonRequired': "Motif d'escalade requis",
    'od.escalationReasonRequiredDesc': 'Veuillez fournir un motif pour l\'escalade.',
    'od.selectTicketEscalate': "Sélectionnez un ticket avant d'escalader.",
    'od.escalationCreatedDesc': 'Escalade {id} créée.',
    'od.escalationFailed': "Échec de l'escalade",
    'od.noConversationLinked': 'Aucune conversation liée',
    'od.noConversationLinkedDesc': "Cette escalade n'a aucune conversation liée. Impossible d'envoyer le message.",
    'od.messageSent': 'Message envoyé',
    'od.messageSentDesc': 'Le client recevra ce message.',
    'od.sendFailed': "Échec de l'envoi",
    'od.subtitleVoiceSimulator': "Testez l'agent vocal IA avec des scénarios complexes, des requêtes multilingues et des contextes émotionnels",
    'od.crmAuthReady': 'Authentification CRM prête',
    'od.calling': 'Appel :',
    'od.selectScenarioVoice': "Sélectionnez un scénario pour tester les capacités de l'IA",
    'od.selectLanguage': 'Sélectionnez la langue',
    'od.langEnglish': 'Anglais',
    'od.langFrench': 'Français',
    'od.langMixed': 'Mixte',
    'od.initiateCall': "Lancer l'appel",
    'od.endCall': "Terminer l'appel",
    'od.processing': 'Traitement',
    'od.active': 'Actif',
    'od.idle': 'Inactif',
    'od.initiateCallToStart': 'Lancez un appel pour commencer les tests',
    'od.listening': 'Écoute...',
    'od.clickToSpeak': 'Cliquez pour dire un message',
    'od.turnOffVoiceMode': 'Désactiver le mode vocal continu',
    'od.turnOnVoiceMode': 'Activer le mode vocal continu',
    'od.aiVoice': 'Voix IA',
    'od.on': 'ACT.',
    'od.off': 'DÉS.',
    'od.thinking': 'Réflexion',
    'od.voiceInputPlaceholder': 'Tapez un message et appuyez sur Entrée (Maj+Entrée pour un saut de ligne), ou utilisez les boutons micro ci-dessus...',
    'od.vsReady': 'Prêt',
    'od.vsProcessing': 'Traitement...',
    'od.vsSpeaking': 'Parle...',
    'od.vsError': 'Erreur micro',
    'od.notSupported': 'Non pris en charge',
    'od.speechRequires': 'La reconnaissance vocale nécessite Chrome, Edge ou Safari 14.1+.',
    'od.micBlocked': 'Microphone bloqué',
    'od.micBlockedDesc': "Autorisez l'accès au microphone dans les paramètres de votre navigateur et réessayez.",
    'od.callInitiated': 'Appel lancé',
    'od.aiVoiceConnected': 'Agent vocal IA connecté. Session : {session}',
    'od.callFailed': "Échec de l'appel",
    'od.callFailedDesc': "Impossible de lancer l'appel IA. Veuillez réessayer.",
    'od.callEscalated': 'Appel escaladé',
    'od.aiVoiceTerminated': 'Session vocale IA terminée.',
    'od.chatSimSubtitle': "Simulateur de test QA — envoie les messages saisis directement au backend de chat IA en direct. Ce n'est pas une véritable session client. Testez l'agent de chat IA avec plus de 30 scénarios incluant des situations complexes, des requêtes multilingues et des contextes émotionnels.",
    'od.qaSimulatorMode': 'Mode simulateur QA',
    'od.userLabel': 'Utilisateur :',
    'od.guest': 'Invité',
    'od.crmAuth': 'Authentification CRM',
    'od.restart': 'Redémarrer',
    'od.connected': 'Connecté',
    'od.aiActive': 'IA active',
    'od.realTime': 'Temps réel',
    'od.voiceReady': 'Voix prête',
    'od.listeningShort': 'Écoute',
    'od.chattingWith': 'Discussion avec :',
    'od.selectScenarioChat': 'Sélectionnez un scénario pour tester les capacités du chat IA',
    'od.chatControls': 'Commandes de chat',
    'od.customerPhone': 'Téléphone du client',
    'od.startChat': 'Démarrer le chat',
    'od.endChat': 'Terminer le chat',
    'od.startChatToTest': 'Démarrez une session de chat pour commencer les tests',
    'od.chatInputPlaceholder': 'Tapez votre message ou sélectionnez un scénario...',
    'od.clear': 'Effacer',
    'od.chatInitiated': 'Chat lancé',
    'od.aiChatConnected': 'Agent de chat IA connecté. Session : {session}',
    'od.chatFailed': 'Échec du chat',
    'od.chatFailedDesc': "Impossible de lancer le chat IA. Veuillez réessayer.",
    'od.chatEscalated': 'Chat escaladé',
    'od.chatEnded': 'Chat terminé',
    'od.aiChatTerminated': 'Session de chat IA terminée.',
  },
  pt: {
    'od.enableSound': 'Ativar som',
    // ── AI-Voice human handoff (Phase C): media state + transcript/summary ──
    'od.mediaJoining': 'Conectando você ao cliente…',
    'od.waitingForCustomerOffer': 'Aguardando o cliente conectar…',
    'od.mediaConnecting': 'Estabelecendo áudio ao vivo…',
    'od.humanCallActive': 'Chamada humana ativa — áudio ao vivo conectado',
    'od.mediaFailed': 'Não foi possível estabelecer o áudio ao vivo. O cliente foi notificado.',
    'od.aiContext': 'Contexto da IA',
    'od.liveTranscript': 'Transcrição ao vivo',
    'od.callSummary': 'Resumo da chamada',
    'od.actionItems': 'Ações a realizar',
    'od.commitments': 'Compromissos',
    'od.speakerAgent': 'Agente',
    'od.speakerCustomer': 'Cliente',
    'od.networkError': 'Erro de rede',
    'od.missingInformation': 'Informações ausentes',
    'od.customer': 'Cliente',
    'od.aiAgent': 'Agente IA',
    'od.escalated': 'Escalado',
    'od.sendMessage': 'Enviar mensagem',
    'od.testScenarios': 'Cenários de teste',
    'od.selectedScenario': 'Cenário selecionado',
    'od.expected': 'Esperado:',
    'od.conversation': 'Conversa',
    'od.sessionLabel': 'Referência da sessão:',
    'od.languageLabel': 'Idioma:',
    'od.intentLabel': 'Intenção:',
    'od.detecting': 'Detectando...',
    'od.testEmailOverride': 'E-mail de teste (substituição)',
    'od.optionalSimulatorOnly': '(opcional — apenas simulador)',
    'od.emailOverridePlaceholder': 'ex. voce@exemplo.com — ignora a busca por telefone',
    'od.customerName': 'Nome do cliente',
    'od.escalatingTo': 'Escalando para {target}: {reason}',
    'od.processingFailed': 'Falha no processamento',
    'od.processFailedFallback': 'Não foi possível processar sua mensagem. Tente novamente.',
    'od.callEnded': 'Chamada encerrada',
    'od.tabBasic': 'Básico',
    'od.tabComplex': 'Complexo',
    'od.tabAmbiguous': 'Ambíguo',
    'od.tabMultilingual': 'Multilíngue',
    'od.tabEmpathy': 'Empatia',
    'od.tabEnvironment': 'Ambiente',
    'od.tabContextual': 'Contextual',
    'od.subtitleUnifiedDesktop': 'Todos os canais, dados de clientes e ferramentas em uma única interface',
    'od.takeBreak': 'Fazer pausa',
    'od.endBreak': 'Encerrar pausa',
    'od.callsToday': 'Chamadas hoje',
    'od.avgHandleTime': 'Tempo médio de atendimento',
    'od.target': 'Meta: {value}',
    'od.firstCallResolution': 'Resolução na primeira chamada',
    'od.customerSatisfaction': 'Satisfação do cliente',
    'od.ticketsResolved': 'Tickets resolvidos',
    'od.performanceExcellent': 'Desempenho: Excelente',
    'od.channelOverview': 'Visão geral dos canais',
    'od.channelVoice': 'Voz',
    'od.channelChat': 'Chat',
    'od.channelEmail': 'E-mail',
    'od.channelSocial': 'Social',
    'od.callControls': 'Controles de chamada',
    'od.answer': 'Atender',
    'od.incomingHandoff': 'Transferência recebida',
    'od.notEligible': 'Você não pode atender esta transferência',
    'od.assignedOther': 'Atribuída a outro operador',
    'od.noLongerAvailable': 'Não está mais disponível',
    'od.handoffExpired': 'A transferência expirou',
    'od.alreadyAcceptedOther': 'Já aceita por outro operador',
    'od.handoffLocked': 'Esta transferência não pode ser processada no seu posto',
    'od.onHold': 'Em espera',
    'od.live': 'Ao vivo',
    'od.connectedNoAudio': 'Conectado — sem áudio ao vivo',
    'od.handoffChannel': 'Transferência de {channel}',
    'od.sourceAiVoice': 'Voz IA',
    'od.sourceAiChat': 'Chat IA',
    'od.handoffToast': 'Transferência de cliente recebida',
    'od.handoffToastUrgent': 'Transferência de cliente urgente',
    'od.opCallIncoming': 'Chamada de operador recebida',
    'od.opCallIncomingVideo': 'Videochamada recebida',
    'od.opCallIncomingVoice': 'Chamada de voz recebida',
    'od.opCallCustomer': 'Cliente',
    'od.opCallDecline': 'Recusar',
    'od.opCallStarting': 'Iniciando…',
    'od.opCallAnswered': 'Chamada atendida',
    'od.opCallConnecting': 'Conectando — aguardando o áudio do chamador',
    'od.opCallAnsweredElsewhere': 'Atendida em outro dispositivo',
    'od.opCallAnsweredElsewhereDesc': 'Esta chamada foi atendida em outro dispositivo.',
    'od.opCallFailed': 'Nao foi possivel atender',
    'od.opCallNoConnection': 'Sem conexao ativa. Reconecte-se e tente novamente.',
    'od.hangUp': 'Desligar',
    'od.resume': 'Retomar',
    'od.hold': 'Em espera',
    'od.mute': 'Silenciar',
    'od.unmute': 'Ativar microfone',
    'od.volume': 'Volume',
    'od.customerInteraction': 'Interação com o cliente - {channel}',
    'od.liveCall': 'Chamada ao vivo',
    'od.issueLabel': 'Problema:',
    'od.prioritySuffix': 'prioridade {priority}',
    'od.customerHistory': 'Histórico do cliente',
    'od.resolved': 'Resolvido',
    'od.pending': 'Pendente',
    'od.callNotes': 'Notas da chamada',
    'od.addNotesPlaceholder': 'Adicione notas sobre esta chamada...',
    'od.quickResponsePlaceholder': 'Resposta rápida...',
    'od.handlingAs': 'Atendendo como {name}',
    'od.callQualityScore': 'Pontuação de qualidade da chamada',
    'od.micMuted': 'O microfone está silenciado',
    'od.saveResolve': 'Salvar e resolver',
    'od.escalate': 'Escalar',
    'od.transfer': 'Transferir',
    'od.readyNext': 'Pronto para a próxima interação',
    'od.waitingIncoming': 'Aguardando interação recebida de {channel}',
    'od.queueWaiting': 'Fila: {count} aguardando',
    'od.tabKB': 'BC',
    'od.tabQuick': 'Rápidas',
    'od.tabTools': 'Ferramentas',
    'od.knowledgeBase': 'Base de conhecimento',
    'od.searchSolutions': 'Pesquisar soluções...',
    'od.noApprovedSolutions': 'Ainda não há soluções aprovadas.',
    'od.noSolutionsMatch': 'Nenhuma solução corresponde à sua pesquisa.',
    'od.clickToInsert': 'Clique para inserir esta solução na sua resposta',
    'od.usesSuffix': '{count} usos',
    'od.quickResponses': 'Respostas rápidas',
    'od.productivityTools': 'Ferramentas de produtividade',
    'od.createTicket': 'Criar ticket',
    'od.createNewTicket': 'Criar novo ticket',
    'od.createTicketDesc': 'Crie um ticket de suporte para o problema atual do cliente',
    'od.titleRequired': 'Título *',
    'od.descriptionRequired': 'Descrição *',
    'od.priority': 'Prioridade',
    'od.category': 'Categoria',
    'od.customerNameEmailPlaceholder': 'Nome ou e-mail do cliente',
    'od.briefIssuePlaceholder': 'Breve descrição do problema',
    'od.detailedIssuePlaceholder': 'Descrição detalhada do problema',
    'od.priorityLow': 'Baixa',
    'od.priorityMedium': 'Média',
    'od.priorityHigh': 'Alta',
    'od.priorityUrgent': 'Urgente',
    'od.catTechnical': 'Suporte técnico',
    'od.catBilling': 'Faturamento',
    'od.catProduct': 'Problemas de produto',
    'od.catAccount': 'Gestão de conta',
    'od.scheduleCallback': 'Agendar retorno',
    'od.scheduleCallbackDesc': 'Agende um retorno de chamada para o cliente',
    'od.customerNameRequired': 'Nome do cliente *',
    'od.phoneNumber': 'Número de telefone',
    'od.customerNamePlaceholder': 'Nome do cliente',
    'od.phonePlaceholder': '+1-555-0123',
    'od.dateRequired': 'Data *',
    'od.timeRequired': 'Hora *',
    'od.notes': 'Notas',
    'od.callbackNotesPlaceholder': 'Notas adicionais para o retorno',
    'od.generateReport': 'Gerar relatório',
    'od.generateReportDesc': 'Gere um relatório de desempenho ou atividade',
    'od.reportType': 'Tipo de relatório',
    'od.timePeriod': 'Período',
    'od.format': 'Formato',
    'od.reportPerformance': 'Relatório de desempenho',
    'od.reportActivity': 'Relatório de atividade',
    'od.reportCustomer': 'Interações com clientes',
    'od.reportResolution': 'Resumo de resoluções',
    'od.periodToday': 'Hoje',
    'od.periodWeek': 'Esta semana',
    'od.periodMonth': 'Este mês',
    'od.periodQuarter': 'Este trimestre',
    'od.screenShare': 'Compartilhar tela',
    'od.screenShareDesc': 'Inicie uma sessão de compartilhamento de tela com o cliente',
    'od.startScreenShare': 'Iniciar compartilhamento de tela',
    'od.screenShareInfo': 'Isso permitirá que o cliente veja sua tela para melhor assistência',
    'od.secureConnection': 'Conexão segura',
    'od.endToEndEncrypted': 'Criptografado de ponta a ponta',
    'od.customerNotified': 'O cliente será notificado',
    'od.transferCall': 'Transferir chamada',
    'od.transferCallDesc': 'Selecione para onde transferir a chamada atual',
    'od.transferTo': 'Transferir para',
    'od.selectTransferDest': 'Selecione o destino da transferência',
    'od.destManager': 'Gerente',
    'od.destBilling': 'Departamento de faturamento',
    'od.destSales': 'Equipe de vendas',
    'od.destSupervisor': 'Supervisor',
    'od.adjustVolume': 'Ajustar volume da chamada',
    'od.adjustVolumeDesc': 'Ajuste o nível de volume da chamada atual',
    'od.volumeLevel': 'Nível de volume: {value}%',
    'od.saveResolveTicket': 'Salvar e resolver ticket',
    'od.saveResolveDesc': 'Adicione notas de resolução e marque este ticket como resolvido',
    'od.resolutionNotesLabel': 'Notas de resolução *',
    'od.resolutionPlaceholder': 'Descreva como o problema foi resolvido...',
    'od.resolveInfo': 'Isso marcará o ticket como resolvido e encerrará a interação atual.',
    'od.escalateTicket': 'Escalar ticket',
    'od.escalateTicketDesc': 'Escale este ticket para um supervisor ou especialista',
    'od.escalationReasonLabel': 'Motivo da escalação *',
    'od.escalationPlaceholder': 'Explique por que este ticket precisa ser escalado...',
    'od.escalateTo': 'Escalar para',
    'od.escAdmin': 'Administrador',
    'od.escalateInfo': 'O ticket será transferido para a equipe selecionada para mais assistência.',
    'od.noIncomingCall': 'Sem chamada recebida',
    'od.noIncomingCallDesc': 'Não há nenhuma chamada recebida para atender.',
    'od.callOnHold': 'Chamada em espera',
    'od.callOnHoldDesc': 'Microfone silenciado. O cliente está em espera.',
    'od.callResumed': 'Chamada retomada',
    'od.callResumedDesc': 'Microfone ativo. O cliente pode ouvi-lo.',
    'od.callDisconnected': 'A chamada foi desconectada.',
    'od.transferTargetRequired': 'Destino da transferência obrigatório',
    'od.transferTargetRequiredDesc': 'Selecione um membro da equipe para transferir.',
    'od.noActiveCall': 'Sem chamada ativa',
    'od.noActiveCallTransfer': 'Não há nenhuma chamada WebRTC ativa para transferir.',
    'od.callTransferred': 'Chamada transferida',
    'od.callTransferredDesc': 'Chamada transferida para o membro da equipe {target}.',
    'od.transferFailed': 'Falha na transferência',
    'od.noSolutionText': 'Sem texto de solução',
    'od.noSolutionTextDesc': 'Esta solução ainda não tem conteúdo inserível.',
    'od.solutionInserted': 'Solução inserida',
    'od.solutionInsertedDesc': '"{title}" adicionada à sua resposta.',
    'od.solution': 'Solução',
    'od.fillTitleDescription': 'Preencha o título e a descrição',
    'od.ticketCreated': 'Ticket criado',
    'od.ticketCreatedDesc': 'Conversa {id} criada.',
    'od.createFailed': 'Falha na criação',
    'od.fillCustomerDateTime': 'Preencha o cliente, a data e a hora',
    'od.callbackScheduled': 'Retorno agendado',
    'od.callbackScheduledDesc': 'Retorno para {customer} salvo.',
    'od.callbackFailed': 'Falha no retorno',
    'od.reportDownloaded': 'Relatório baixado',
    'od.reportDownloadedDesc': 'Relatório gerado a partir de dados ao vivo.',
    'od.reportFailed': 'Falha no relatório',
    'od.screenShareRequiresCall': 'O compartilhamento de tela requer uma chamada WebRTC ativa com o cliente.',
    'od.screenShareActive': 'Compartilhamento de tela ativo',
    'od.screenShareActiveDesc': 'Sua tela agora está visível para o cliente.',
    'od.screenShareFailed': 'Falha no compartilhamento de tela',
    'od.screenShareFailedFallback': 'Não foi possível iniciar o compartilhamento de tela.',
    'od.resolutionNotesRequired': 'Notas de resolução obrigatórias',
    'od.resolutionNotesRequiredDesc': 'Forneça notas de resolução antes de salvar.',
    'od.noActiveTicket': 'Sem ticket ativo',
    'od.selectTicketResolve': 'Selecione um ticket antes de resolver.',
    'od.ticketResolved': 'Ticket resolvido',
    'od.ticketResolvedDesc': 'Ticket {id} resolvido e salvo.',
    'od.resolutionFailed': 'Falha na resolução',
    'od.escalationReasonRequired': 'Motivo da escalação obrigatório',
    'od.escalationReasonRequiredDesc': 'Forneça um motivo para a escalação.',
    'od.selectTicketEscalate': 'Selecione um ticket antes de escalar.',
    'od.escalationCreatedDesc': 'Escalação {id} criada.',
    'od.escalationFailed': 'Falha na escalação',
    'od.noConversationLinked': 'Sem conversa vinculada',
    'od.noConversationLinkedDesc': 'Esta escalação não tem conversa vinculada. Não é possível enviar a mensagem.',
    'od.messageSent': 'Mensagem enviada',
    'od.messageSentDesc': 'O cliente receberá esta mensagem.',
    'od.sendFailed': 'Falha no envio',
    'od.subtitleVoiceSimulator': 'Teste o agente de voz IA com cenários complexos, consultas multilíngues e contextos emocionais',
    'od.crmAuthReady': 'Autenticação CRM pronta',
    'od.calling': 'Chamando:',
    'od.selectScenarioVoice': 'Selecione um cenário para testar as capacidades da IA',
    'od.selectLanguage': 'Selecione o idioma',
    'od.langEnglish': 'Inglês',
    'od.langFrench': 'Francês',
    'od.langMixed': 'Misto',
    'od.initiateCall': 'Iniciar chamada',
    'od.endCall': 'Encerrar chamada',
    'od.processing': 'Processando',
    'od.active': 'Ativo',
    'od.idle': 'Inativo',
    'od.initiateCallToStart': 'Inicie uma chamada para começar os testes',
    'od.listening': 'Ouvindo...',
    'od.clickToSpeak': 'Clique para falar uma mensagem',
    'od.turnOffVoiceMode': 'Desativar o modo de voz contínuo',
    'od.turnOnVoiceMode': 'Ativar o modo de voz contínuo',
    'od.aiVoice': 'Voz IA',
    'od.on': 'LIG.',
    'od.off': 'DESL.',
    'od.thinking': 'Pensando',
    'od.voiceInputPlaceholder': 'Digite uma mensagem e pressione Enter (Shift+Enter para nova linha), ou use os botões de microfone acima...',
    'od.vsReady': 'Pronto',
    'od.vsProcessing': 'Processando...',
    'od.vsSpeaking': 'Falando...',
    'od.vsError': 'Erro de microfone',
    'od.notSupported': 'Não suportado',
    'od.speechRequires': 'O reconhecimento de voz requer Chrome, Edge ou Safari 14.1+.',
    'od.micBlocked': 'Microfone bloqueado',
    'od.micBlockedDesc': 'Permita o acesso ao microfone nas configurações do navegador e tente novamente.',
    'od.callInitiated': 'Chamada iniciada',
    'od.aiVoiceConnected': 'Agente de voz IA conectado. Sessão: {session}',
    'od.callFailed': 'Falha na chamada',
    'od.callFailedDesc': 'Não foi possível iniciar a chamada IA. Tente novamente.',
    'od.callEscalated': 'Chamada escalada',
    'od.aiVoiceTerminated': 'Sessão de voz IA encerrada.',
    'od.chatSimSubtitle': 'Simulador de testes de QA — envia mensagens digitadas diretamente ao backend de chat IA ao vivo. Não é uma sessão real de cliente. Teste o agente de chat IA com mais de 30 cenários, incluindo situações complexas, consultas multilíngues e contextos emocionais.',
    'od.qaSimulatorMode': 'Modo simulador de QA',
    'od.userLabel': 'Usuário:',
    'od.guest': 'Convidado',
    'od.crmAuth': 'Autenticação CRM',
    'od.restart': 'Reiniciar',
    'od.connected': 'Conectado',
    'od.aiActive': 'IA ativa',
    'od.realTime': 'Tempo real',
    'od.voiceReady': 'Voz pronta',
    'od.listeningShort': 'Ouvindo',
    'od.chattingWith': 'Conversando com:',
    'od.selectScenarioChat': 'Selecione um cenário para testar as capacidades do chat IA',
    'od.chatControls': 'Controles de chat',
    'od.customerPhone': 'Telefone do cliente',
    'od.startChat': 'Iniciar chat',
    'od.endChat': 'Encerrar chat',
    'od.startChatToTest': 'Inicie uma sessão de chat para começar os testes',
    'od.chatInputPlaceholder': 'Digite sua mensagem ou selecione um cenário...',
    'od.clear': 'Limpar',
    'od.chatInitiated': 'Chat iniciado',
    'od.aiChatConnected': 'Agente de chat IA conectado. Sessão: {session}',
    'od.chatFailed': 'Falha no chat',
    'od.chatFailedDesc': 'Não foi possível iniciar o chat IA. Tente novamente.',
    'od.chatEscalated': 'Chat escalado',
    'od.chatEnded': 'Chat encerrado',
    'od.aiChatTerminated': 'Sessão de chat IA encerrada.',
  },
  de: {
    'od.enableSound': 'Ton aktivieren',
    // ── AI-Voice human handoff (Phase C): media state + transcript/summary ──
    'od.mediaJoining': 'Sie werden mit dem Kunden verbunden…',
    'od.waitingForCustomerOffer': 'Warten auf die Verbindung des Kunden…',
    'od.mediaConnecting': 'Live-Audio wird hergestellt…',
    'od.humanCallActive': 'Menschlicher Anruf aktiv — Live-Audio verbunden',
    'od.mediaFailed': 'Live-Audio konnte nicht hergestellt werden. Der Kunde wurde benachrichtigt.',
    'od.aiContext': 'KI-Kontext',
    'od.liveTranscript': 'Live-Transkript',
    'od.callSummary': 'Anrufzusammenfassung',
    'od.actionItems': 'Aufgaben',
    'od.commitments': 'Zusagen',
    'od.speakerAgent': 'Agent',
    'od.speakerCustomer': 'Kunde',
    'od.networkError': 'Netzwerkfehler',
    'od.missingInformation': 'Fehlende Informationen',
    'od.customer': 'Kunde',
    'od.aiAgent': 'KI-Agent',
    'od.escalated': 'Eskaliert',
    'od.sendMessage': 'Nachricht senden',
    'od.testScenarios': 'Testszenarien',
    'od.selectedScenario': 'Ausgewähltes Szenario',
    'od.expected': 'Erwartet:',
    'od.conversation': 'Konversation',
    'od.sessionLabel': 'Sitzungsreferenz:',
    'od.languageLabel': 'Sprache:',
    'od.intentLabel': 'Absicht:',
    'od.detecting': 'Erkennung...',
    'od.testEmailOverride': 'Test-E-Mail (Überschreibung)',
    'od.optionalSimulatorOnly': '(optional — nur Simulator)',
    'od.emailOverridePlaceholder': 'z. B. sie@beispiel.com — umgeht die Telefonsuche',
    'od.customerName': 'Kundenname',
    'od.escalatingTo': 'Eskalation an {target}: {reason}',
    'od.processingFailed': 'Verarbeitung fehlgeschlagen',
    'od.processFailedFallback': 'Ihre Nachricht konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.',
    'od.callEnded': 'Anruf beendet',
    'od.tabBasic': 'Basis',
    'od.tabComplex': 'Komplex',
    'od.tabAmbiguous': 'Mehrdeutig',
    'od.tabMultilingual': 'Mehrsprachig',
    'od.tabEmpathy': 'Empathie',
    'od.tabEnvironment': 'Umgebung',
    'od.tabContextual': 'Kontextbezogen',
    'od.subtitleUnifiedDesktop': 'Alle Kanäle, Kundendaten und Tools in einer Oberfläche',
    'od.takeBreak': 'Pause machen',
    'od.endBreak': 'Pause beenden',
    'od.callsToday': 'Anrufe heute',
    'od.avgHandleTime': 'Durchschn. Bearbeitungszeit',
    'od.target': 'Ziel: {value}',
    'od.firstCallResolution': 'Lösung beim ersten Anruf',
    'od.customerSatisfaction': 'Kundenzufriedenheit',
    'od.ticketsResolved': 'Gelöste Tickets',
    'od.performanceExcellent': 'Leistung: Ausgezeichnet',
    'od.channelOverview': 'Kanalübersicht',
    'od.channelVoice': 'Sprache',
    'od.channelChat': 'Chat',
    'od.channelEmail': 'E-Mail',
    'od.channelSocial': 'Soziale Medien',
    'od.callControls': 'Anrufsteuerung',
    'od.answer': 'Annehmen',
    'od.incomingHandoff': 'Eingehende Übergabe',
    'od.notEligible': 'Sie können diese Übergabe nicht annehmen',
    'od.assignedOther': 'Einem anderen Operator zugewiesen',
    'od.noLongerAvailable': 'Nicht mehr verfügbar',
    'od.handoffExpired': 'Übergabe abgelaufen',
    'od.alreadyAcceptedOther': 'Bereits von einem anderen Operator angenommen',
    'od.handoffLocked': 'Diese Übergabe kann von Ihrem Arbeitsplatz nicht bearbeitet werden',
    'od.onHold': 'Gehalten',
    'od.live': 'Live',
    'od.connectedNoAudio': 'Verbunden — kein Live-Audio',
    'od.handoffChannel': '{channel}-Übergabe',
    'od.sourceAiVoice': 'KI-Sprache',
    'od.sourceAiChat': 'KI-Chat',
    'od.handoffToast': 'Eingehende Kundenübergabe',
    'od.handoffToastUrgent': 'Dringende Kundenübergabe',
    'od.opCallIncoming': 'Eingehender Operator-Anruf',
    'od.opCallIncomingVideo': 'Eingehender Videoanruf',
    'od.opCallIncomingVoice': 'Eingehender Sprachanruf',
    'od.opCallCustomer': 'Kunde',
    'od.opCallDecline': 'Ablehnen',
    'od.opCallStarting': 'Wird gestartet…',
    'od.opCallAnswered': 'Anruf angenommen',
    'od.opCallConnecting': 'Verbinden – warte auf Anrufer-Audio',
    'od.opCallAnsweredElsewhere': 'Anderswo angenommen',
    'od.opCallAnsweredElsewhereDesc': 'Dieser Anruf wurde auf einem anderen Gerät angenommen.',
    'od.opCallFailed': 'Anruf konnte nicht angenommen werden',
    'od.opCallNoConnection': 'Keine aktive Verbindung. Bitte neu verbinden und erneut versuchen.',
    'od.hangUp': 'Auflegen',
    'od.resume': 'Fortsetzen',
    'od.hold': 'Halten',
    'od.mute': 'Stummschalten',
    'od.unmute': 'Stummschaltung aufheben',
    'od.volume': 'Lautstärke',
    'od.customerInteraction': 'Kundeninteraktion - {channel}',
    'od.liveCall': 'Live-Anruf',
    'od.issueLabel': 'Problem:',
    'od.prioritySuffix': 'Priorität {priority}',
    'od.customerHistory': 'Kundenhistorie',
    'od.resolved': 'Gelöst',
    'od.pending': 'Ausstehend',
    'od.callNotes': 'Anrufnotizen',
    'od.addNotesPlaceholder': 'Notizen zu diesem Anruf hinzufügen...',
    'od.quickResponsePlaceholder': 'Schnellantwort...',
    'od.handlingAs': 'Bearbeitung als {name}',
    'od.callQualityScore': 'Anrufqualitätswert',
    'od.micMuted': 'Das Mikrofon ist stummgeschaltet',
    'od.saveResolve': 'Speichern & lösen',
    'od.escalate': 'Eskalieren',
    'od.transfer': 'Weiterleiten',
    'od.readyNext': 'Bereit für die nächste Interaktion',
    'od.waitingIncoming': 'Warten auf eingehende {channel}-Interaktion',
    'od.queueWaiting': 'Warteschlange: {count} wartend',
    'od.tabKB': 'WB',
    'od.tabQuick': 'Schnell',
    'od.tabTools': 'Tools',
    'od.knowledgeBase': 'Wissensdatenbank',
    'od.searchSolutions': 'Lösungen suchen...',
    'od.noApprovedSolutions': 'Noch keine genehmigten Lösungen.',
    'od.noSolutionsMatch': 'Keine Lösungen entsprechen Ihrer Suche.',
    'od.clickToInsert': 'Klicken Sie, um diese Lösung in Ihre Antwort einzufügen',
    'od.usesSuffix': '{count} Verwendungen',
    'od.quickResponses': 'Schnellantworten',
    'od.productivityTools': 'Produktivitätstools',
    'od.createTicket': 'Ticket erstellen',
    'od.createNewTicket': 'Neues Ticket erstellen',
    'od.createTicketDesc': 'Erstellen Sie ein Support-Ticket für das aktuelle Kundenproblem',
    'od.titleRequired': 'Titel *',
    'od.descriptionRequired': 'Beschreibung *',
    'od.priority': 'Priorität',
    'od.category': 'Kategorie',
    'od.customerNameEmailPlaceholder': 'Kundenname oder E-Mail',
    'od.briefIssuePlaceholder': 'Kurze Beschreibung des Problems',
    'od.detailedIssuePlaceholder': 'Detaillierte Beschreibung des Problems',
    'od.priorityLow': 'Niedrig',
    'od.priorityMedium': 'Mittel',
    'od.priorityHigh': 'Hoch',
    'od.priorityUrgent': 'Dringend',
    'od.catTechnical': 'Technischer Support',
    'od.catBilling': 'Abrechnung',
    'od.catProduct': 'Produktprobleme',
    'od.catAccount': 'Kontoverwaltung',
    'od.scheduleCallback': 'Rückruf planen',
    'od.scheduleCallbackDesc': 'Planen Sie einen Rückruf für den Kunden',
    'od.customerNameRequired': 'Kundenname *',
    'od.phoneNumber': 'Telefonnummer',
    'od.customerNamePlaceholder': 'Kundenname',
    'od.phonePlaceholder': '+1-555-0123',
    'od.dateRequired': 'Datum *',
    'od.timeRequired': 'Uhrzeit *',
    'od.notes': 'Notizen',
    'od.callbackNotesPlaceholder': 'Zusätzliche Notizen für den Rückruf',
    'od.generateReport': 'Bericht erstellen',
    'od.generateReportDesc': 'Erstellen Sie einen Leistungs- oder Aktivitätsbericht',
    'od.reportType': 'Berichtstyp',
    'od.timePeriod': 'Zeitraum',
    'od.format': 'Format',
    'od.reportPerformance': 'Leistungsbericht',
    'od.reportActivity': 'Aktivitätsbericht',
    'od.reportCustomer': 'Kundeninteraktionen',
    'od.reportResolution': 'Lösungszusammenfassung',
    'od.periodToday': 'Heute',
    'od.periodWeek': 'Diese Woche',
    'od.periodMonth': 'Diesen Monat',
    'od.periodQuarter': 'Dieses Quartal',
    'od.screenShare': 'Bildschirmfreigabe',
    'od.screenShareDesc': 'Starten Sie eine Bildschirmfreigabe-Sitzung mit dem Kunden',
    'od.startScreenShare': 'Bildschirmfreigabe starten',
    'od.screenShareInfo': 'Dadurch kann der Kunde Ihren Bildschirm für eine bessere Unterstützung sehen',
    'od.secureConnection': 'Sichere Verbindung',
    'od.endToEndEncrypted': 'Ende-zu-Ende-verschlüsselt',
    'od.customerNotified': 'Der Kunde wird benachrichtigt',
    'od.transferCall': 'Anruf weiterleiten',
    'od.transferCallDesc': 'Wählen Sie, wohin der aktuelle Anruf weitergeleitet werden soll',
    'od.transferTo': 'Weiterleiten an',
    'od.selectTransferDest': 'Weiterleitungsziel auswählen',
    'od.destManager': 'Manager',
    'od.destBilling': 'Abrechnungsabteilung',
    'od.destSales': 'Vertriebsteam',
    'od.destSupervisor': 'Vorgesetzter',
    'od.adjustVolume': 'Anruflautstärke anpassen',
    'od.adjustVolumeDesc': 'Passen Sie die Lautstärke des aktuellen Anrufs an',
    'od.volumeLevel': 'Lautstärke: {value} %',
    'od.saveResolveTicket': 'Ticket speichern & lösen',
    'od.saveResolveDesc': 'Fügen Sie Lösungsnotizen hinzu und markieren Sie dieses Ticket als gelöst',
    'od.resolutionNotesLabel': 'Lösungsnotizen *',
    'od.resolutionPlaceholder': 'Beschreiben Sie, wie das Problem gelöst wurde...',
    'od.resolveInfo': 'Dadurch wird das Ticket als gelöst markiert und die aktuelle Interaktion geschlossen.',
    'od.escalateTicket': 'Ticket eskalieren',
    'od.escalateTicketDesc': 'Eskalieren Sie dieses Ticket an einen Vorgesetzten oder Spezialisten',
    'od.escalationReasonLabel': 'Eskalationsgrund *',
    'od.escalationPlaceholder': 'Erklären Sie, warum dieses Ticket eskaliert werden muss...',
    'od.escalateTo': 'Eskalieren an',
    'od.escAdmin': 'Administrator',
    'od.escalateInfo': 'Das Ticket wird zur weiteren Unterstützung an das ausgewählte Team weitergeleitet.',
    'od.noIncomingCall': 'Kein eingehender Anruf',
    'od.noIncomingCallDesc': 'Es gibt keinen eingehenden Anruf zum Annehmen.',
    'od.callOnHold': 'Anruf gehalten',
    'od.callOnHoldDesc': 'Mikrofon stummgeschaltet. Der Kunde wird gehalten.',
    'od.callResumed': 'Anruf fortgesetzt',
    'od.callResumedDesc': 'Mikrofon aktiv. Der Kunde kann Sie hören.',
    'od.callDisconnected': 'Der Anruf wurde getrennt.',
    'od.transferTargetRequired': 'Weiterleitungsziel erforderlich',
    'od.transferTargetRequiredDesc': 'Bitte wählen Sie einen Mitarbeiter zum Weiterleiten aus.',
    'od.noActiveCall': 'Kein aktiver Anruf',
    'od.noActiveCallTransfer': 'Es gibt keinen aktiven WebRTC-Anruf zum Weiterleiten.',
    'od.callTransferred': 'Anruf weitergeleitet',
    'od.callTransferredDesc': 'Anruf an Mitarbeiter {target} weitergeleitet.',
    'od.transferFailed': 'Weiterleitung fehlgeschlagen',
    'od.noSolutionText': 'Kein Lösungstext',
    'od.noSolutionTextDesc': 'Diese Lösung hat noch keinen einfügbaren Inhalt.',
    'od.solutionInserted': 'Lösung eingefügt',
    'od.solutionInsertedDesc': '„{title}" zu Ihrer Antwort hinzugefügt.',
    'od.solution': 'Lösung',
    'od.fillTitleDescription': 'Bitte füllen Sie Titel und Beschreibung aus',
    'od.ticketCreated': 'Ticket erstellt',
    'od.ticketCreatedDesc': 'Konversation {id} erstellt.',
    'od.createFailed': 'Erstellung fehlgeschlagen',
    'od.fillCustomerDateTime': 'Bitte füllen Sie Kunde, Datum und Uhrzeit aus',
    'od.callbackScheduled': 'Rückruf geplant',
    'od.callbackScheduledDesc': 'Rückruf für {customer} gespeichert.',
    'od.callbackFailed': 'Rückruf fehlgeschlagen',
    'od.reportDownloaded': 'Bericht heruntergeladen',
    'od.reportDownloadedDesc': 'Bericht aus Live-Daten erstellt.',
    'od.reportFailed': 'Bericht fehlgeschlagen',
    'od.screenShareRequiresCall': 'Die Bildschirmfreigabe erfordert einen aktiven WebRTC-Anruf mit dem Kunden.',
    'od.screenShareActive': 'Bildschirmfreigabe aktiv',
    'od.screenShareActiveDesc': 'Ihr Bildschirm ist jetzt für den Kunden sichtbar.',
    'od.screenShareFailed': 'Bildschirmfreigabe fehlgeschlagen',
    'od.screenShareFailedFallback': 'Bildschirmfreigabe konnte nicht gestartet werden.',
    'od.resolutionNotesRequired': 'Lösungsnotizen erforderlich',
    'od.resolutionNotesRequiredDesc': 'Bitte geben Sie vor dem Speichern Lösungsnotizen an.',
    'od.noActiveTicket': 'Kein aktives Ticket',
    'od.selectTicketResolve': 'Wählen Sie ein Ticket aus, bevor Sie es lösen.',
    'od.ticketResolved': 'Ticket gelöst',
    'od.ticketResolvedDesc': 'Ticket {id} gelöst und gespeichert.',
    'od.resolutionFailed': 'Lösung fehlgeschlagen',
    'od.escalationReasonRequired': 'Eskalationsgrund erforderlich',
    'od.escalationReasonRequiredDesc': 'Bitte geben Sie einen Grund für die Eskalation an.',
    'od.selectTicketEscalate': 'Wählen Sie ein Ticket aus, bevor Sie eskalieren.',
    'od.escalationCreatedDesc': 'Eskalation {id} erstellt.',
    'od.escalationFailed': 'Eskalation fehlgeschlagen',
    'od.noConversationLinked': 'Keine verknüpfte Konversation',
    'od.noConversationLinkedDesc': 'Diese Eskalation hat keine verknüpfte Konversation. Nachricht kann nicht gesendet werden.',
    'od.messageSent': 'Nachricht gesendet',
    'od.messageSentDesc': 'Der Kunde erhält diese Nachricht.',
    'od.sendFailed': 'Senden fehlgeschlagen',
    'od.subtitleVoiceSimulator': 'Testen Sie den KI-Sprachagenten mit komplexen Szenarien, mehrsprachigen Anfragen und emotionalen Kontexten',
    'od.crmAuthReady': 'CRM-Authentifizierung bereit',
    'od.calling': 'Anruf:',
    'od.selectScenarioVoice': 'Wählen Sie ein Szenario, um die KI-Fähigkeiten zu testen',
    'od.selectLanguage': 'Sprache auswählen',
    'od.langEnglish': 'Englisch',
    'od.langFrench': 'Französisch',
    'od.langMixed': 'Gemischt',
    'od.initiateCall': 'Anruf starten',
    'od.endCall': 'Anruf beenden',
    'od.processing': 'Verarbeitung',
    'od.active': 'Aktiv',
    'od.idle': 'Inaktiv',
    'od.initiateCallToStart': 'Starten Sie einen Anruf, um mit dem Testen zu beginnen',
    'od.listening': 'Hört zu...',
    'od.clickToSpeak': 'Klicken Sie, um eine Nachricht zu sprechen',
    'od.turnOffVoiceMode': 'Kontinuierlichen Sprachmodus deaktivieren',
    'od.turnOnVoiceMode': 'Kontinuierlichen Sprachmodus aktivieren',
    'od.aiVoice': 'KI-Sprache',
    'od.on': 'EIN',
    'od.off': 'AUS',
    'od.thinking': 'Denkt nach',
    'od.voiceInputPlaceholder': 'Geben Sie eine Nachricht ein und drücken Sie Enter (Umschalt+Enter für Zeilenumbruch), oder verwenden Sie die Mikrofontasten oben...',
    'od.vsReady': 'Bereit',
    'od.vsProcessing': 'Verarbeitung...',
    'od.vsSpeaking': 'Spricht...',
    'od.vsError': 'Mikrofonfehler',
    'od.notSupported': 'Nicht unterstützt',
    'od.speechRequires': 'Die Spracherkennung erfordert Chrome, Edge oder Safari 14.1+.',
    'od.micBlocked': 'Mikrofon blockiert',
    'od.micBlockedDesc': 'Erlauben Sie den Mikrofonzugriff in Ihren Browsereinstellungen und versuchen Sie es erneut.',
    'od.callInitiated': 'Anruf gestartet',
    'od.aiVoiceConnected': 'KI-Sprachagent verbunden. Sitzung: {session}',
    'od.callFailed': 'Anruf fehlgeschlagen',
    'od.callFailedDesc': 'KI-Anruf konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
    'od.callEscalated': 'Anruf eskaliert',
    'od.aiVoiceTerminated': 'KI-Sprachsitzung beendet.',
    'od.chatSimSubtitle': 'QA-Test-Simulator — sendet eingegebene Nachrichten direkt an das Live-KI-Chat-Backend. Keine echte Kundensitzung. Testen Sie den KI-Chat-Agenten mit über 30 Szenarien, einschließlich komplexer Situationen, mehrsprachiger Anfragen und emotionaler Kontexte.',
    'od.qaSimulatorMode': 'QA-Simulatormodus',
    'od.userLabel': 'Benutzer:',
    'od.guest': 'Gast',
    'od.crmAuth': 'CRM-Authentifizierung',
    'od.restart': 'Neu starten',
    'od.connected': 'Verbunden',
    'od.aiActive': 'KI aktiv',
    'od.realTime': 'Echtzeit',
    'od.voiceReady': 'Sprache bereit',
    'od.listeningShort': 'Hört zu',
    'od.chattingWith': 'Chatten mit:',
    'od.selectScenarioChat': 'Wählen Sie ein Szenario, um die KI-Chat-Fähigkeiten zu testen',
    'od.chatControls': 'Chat-Steuerung',
    'od.customerPhone': 'Kundentelefon',
    'od.startChat': 'Chat starten',
    'od.endChat': 'Chat beenden',
    'od.startChatToTest': 'Starten Sie eine Chat-Sitzung, um mit dem Testen zu beginnen',
    'od.chatInputPlaceholder': 'Geben Sie Ihre Nachricht ein oder wählen Sie ein Szenario...',
    'od.clear': 'Löschen',
    'od.chatInitiated': 'Chat gestartet',
    'od.aiChatConnected': 'KI-Chat-Agent verbunden. Sitzung: {session}',
    'od.chatFailed': 'Chat fehlgeschlagen',
    'od.chatFailedDesc': 'KI-Chat konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
    'od.chatEscalated': 'Chat eskaliert',
    'od.chatEnded': 'Chat beendet',
    'od.aiChatTerminated': 'KI-Chat-Sitzung beendet.',
  },
  it: {
    'od.enableSound': 'Attiva suono',
    // ── AI-Voice human handoff (Phase C): media state + transcript/summary ──
    'od.mediaJoining': 'Ti stiamo collegando al cliente…',
    'od.waitingForCustomerOffer': 'In attesa della connessione del cliente…',
    'od.mediaConnecting': 'Connessione audio in tempo reale…',
    'od.humanCallActive': 'Chiamata con operatore attiva — audio dal vivo connesso',
    'od.mediaFailed': 'Impossibile stabilire l\'audio dal vivo. Il cliente è stato avvisato.',
    'od.aiContext': 'Contesto IA',
    'od.liveTranscript': 'Trascrizione dal vivo',
    'od.callSummary': 'Riepilogo della chiamata',
    'od.actionItems': 'Attività da svolgere',
    'od.commitments': 'Impegni',
    'od.speakerAgent': 'Operatore',
    'od.speakerCustomer': 'Cliente',
    'od.networkError': 'Errore di rete',
    'od.missingInformation': 'Informazioni mancanti',
    'od.customer': 'Cliente',
    'od.aiAgent': 'Agente IA',
    'od.escalated': 'Inoltrato',
    'od.sendMessage': 'Invia messaggio',
    'od.testScenarios': 'Scenari di test',
    'od.selectedScenario': 'Scenario selezionato',
    'od.expected': 'Previsto:',
    'od.conversation': 'Conversazione',
    'od.sessionLabel': 'Riferimento sessione:',
    'od.languageLabel': 'Lingua:',
    'od.intentLabel': 'Intento:',
    'od.detecting': 'Rilevamento...',
    'od.testEmailOverride': 'E-mail di test (sostituzione)',
    'od.optionalSimulatorOnly': '(facoltativo — solo simulatore)',
    'od.emailOverridePlaceholder': 'es. tu@esempio.com — ignora la ricerca per telefono',
    'od.customerName': 'Nome del cliente',
    'od.escalatingTo': 'Inoltro a {target}: {reason}',
    'od.processingFailed': 'Elaborazione non riuscita',
    'od.processFailedFallback': 'Impossibile elaborare il messaggio. Riprova.',
    'od.callEnded': 'Chiamata terminata',
    'od.tabBasic': 'Base',
    'od.tabComplex': 'Complesso',
    'od.tabAmbiguous': 'Ambiguo',
    'od.tabMultilingual': 'Multilingue',
    'od.tabEmpathy': 'Empatia',
    'od.tabEnvironment': 'Ambiente',
    'od.tabContextual': 'Contestuale',
    'od.subtitleUnifiedDesktop': 'Tutti i canali, i dati dei clienti e gli strumenti in un\'unica interfaccia',
    'od.takeBreak': 'Fai una pausa',
    'od.endBreak': 'Termina pausa',
    'od.callsToday': 'Chiamate oggi',
    'od.avgHandleTime': 'Tempo medio di gestione',
    'od.target': 'Obiettivo: {value}',
    'od.firstCallResolution': 'Risoluzione alla prima chiamata',
    'od.customerSatisfaction': 'Soddisfazione del cliente',
    'od.ticketsResolved': 'Ticket risolti',
    'od.performanceExcellent': 'Prestazioni: Eccellenti',
    'od.channelOverview': 'Panoramica dei canali',
    'od.channelVoice': 'Voce',
    'od.channelChat': 'Chat',
    'od.channelEmail': 'E-mail',
    'od.channelSocial': 'Social',
    'od.callControls': 'Controlli chiamata',
    'od.answer': 'Rispondi',
    'od.incomingHandoff': 'Trasferimento in arrivo',
    'od.notEligible': 'Non puoi gestire questo trasferimento',
    'od.assignedOther': 'Assegnato a un altro operatore',
    'od.noLongerAvailable': 'Non più disponibile',
    'od.handoffExpired': 'Trasferimento scaduto',
    'od.alreadyAcceptedOther': 'Già accettato da un altro operatore',
    'od.handoffLocked': 'Questo trasferimento non è gestibile dalla tua postazione',
    'od.onHold': 'In attesa',
    'od.live': 'In diretta',
    'od.connectedNoAudio': 'Connesso — nessun audio in diretta',
    'od.handoffChannel': 'Trasferimento {channel}',
    'od.sourceAiVoice': 'Voce IA',
    'od.sourceAiChat': 'Chat IA',
    'od.handoffToast': 'Trasferimento cliente in arrivo',
    'od.handoffToastUrgent': 'Trasferimento cliente urgente',
    'od.opCallIncoming': 'Chiamata operatore in arrivo',
    'od.opCallIncomingVideo': 'Videochiamata in arrivo',
    'od.opCallIncomingVoice': 'Chiamata vocale in arrivo',
    'od.opCallCustomer': 'Cliente',
    'od.opCallDecline': 'Rifiuta',
    'od.opCallStarting': 'Avvio…',
    'od.opCallAnswered': 'Chiamata risposta',
    'od.opCallConnecting': 'Connessione — in attesa dell’audio del chiamante',
    'od.opCallAnsweredElsewhere': 'Risposta altrove',
    'od.opCallAnsweredElsewhereDesc': 'Questa chiamata ha ricevuto risposta su un altro dispositivo.',
    'od.opCallFailed': 'Impossibile rispondere',
    'od.opCallNoConnection': 'Nessuna connessione attiva. Riconnettiti e riprova.',
    'od.hangUp': 'Riaggancia',
    'od.resume': 'Riprendi',
    'od.hold': 'In attesa',
    'od.mute': 'Disattiva microfono',
    'od.unmute': 'Attiva microfono',
    'od.volume': 'Volume',
    'od.customerInteraction': 'Interazione con il cliente - {channel}',
    'od.liveCall': 'Chiamata in diretta',
    'od.issueLabel': 'Problema:',
    'od.prioritySuffix': 'priorità {priority}',
    'od.customerHistory': 'Cronologia del cliente',
    'od.resolved': 'Risolto',
    'od.pending': 'In sospeso',
    'od.callNotes': 'Note della chiamata',
    'od.addNotesPlaceholder': 'Aggiungi note su questa chiamata...',
    'od.quickResponsePlaceholder': 'Risposta rapida...',
    'od.handlingAs': 'Gestione come {name}',
    'od.callQualityScore': 'Punteggio qualità chiamata',
    'od.micMuted': 'Il microfono è disattivato',
    'od.saveResolve': 'Salva e risolvi',
    'od.escalate': 'Inoltra',
    'od.transfer': 'Trasferisci',
    'od.readyNext': 'Pronto per la prossima interazione',
    'od.waitingIncoming': 'In attesa di interazione in arrivo da {channel}',
    'od.queueWaiting': 'Coda: {count} in attesa',
    'od.tabKB': 'BC',
    'od.tabQuick': 'Rapide',
    'od.tabTools': 'Strumenti',
    'od.knowledgeBase': 'Base di conoscenza',
    'od.searchSolutions': 'Cerca soluzioni...',
    'od.noApprovedSolutions': 'Ancora nessuna soluzione approvata.',
    'od.noSolutionsMatch': 'Nessuna soluzione corrisponde alla tua ricerca.',
    'od.clickToInsert': 'Fai clic per inserire questa soluzione nella tua risposta',
    'od.usesSuffix': '{count} utilizzi',
    'od.quickResponses': 'Risposte rapide',
    'od.productivityTools': 'Strumenti di produttività',
    'od.createTicket': 'Crea ticket',
    'od.createNewTicket': 'Crea nuovo ticket',
    'od.createTicketDesc': 'Crea un ticket di assistenza per il problema attuale del cliente',
    'od.titleRequired': 'Titolo *',
    'od.descriptionRequired': 'Descrizione *',
    'od.priority': 'Priorità',
    'od.category': 'Categoria',
    'od.customerNameEmailPlaceholder': 'Nome o e-mail del cliente',
    'od.briefIssuePlaceholder': 'Breve descrizione del problema',
    'od.detailedIssuePlaceholder': 'Descrizione dettagliata del problema',
    'od.priorityLow': 'Bassa',
    'od.priorityMedium': 'Media',
    'od.priorityHigh': 'Alta',
    'od.priorityUrgent': 'Urgente',
    'od.catTechnical': 'Supporto tecnico',
    'od.catBilling': 'Fatturazione',
    'od.catProduct': 'Problemi di prodotto',
    'od.catAccount': 'Gestione account',
    'od.scheduleCallback': 'Pianifica richiamata',
    'od.scheduleCallbackDesc': 'Pianifica una richiamata per il cliente',
    'od.customerNameRequired': 'Nome del cliente *',
    'od.phoneNumber': 'Numero di telefono',
    'od.customerNamePlaceholder': 'Nome del cliente',
    'od.phonePlaceholder': '+1-555-0123',
    'od.dateRequired': 'Data *',
    'od.timeRequired': 'Ora *',
    'od.notes': 'Note',
    'od.callbackNotesPlaceholder': 'Note aggiuntive per la richiamata',
    'od.generateReport': 'Genera report',
    'od.generateReportDesc': 'Genera un report di prestazioni o attività',
    'od.reportType': 'Tipo di report',
    'od.timePeriod': 'Periodo',
    'od.format': 'Formato',
    'od.reportPerformance': 'Report prestazioni',
    'od.reportActivity': 'Report attività',
    'od.reportCustomer': 'Interazioni con i clienti',
    'od.reportResolution': 'Riepilogo risoluzioni',
    'od.periodToday': 'Oggi',
    'od.periodWeek': 'Questa settimana',
    'od.periodMonth': 'Questo mese',
    'od.periodQuarter': 'Questo trimestre',
    'od.screenShare': 'Condivisione schermo',
    'od.screenShareDesc': 'Avvia una sessione di condivisione schermo con il cliente',
    'od.startScreenShare': 'Avvia condivisione schermo',
    'od.screenShareInfo': 'Questo consentirà al cliente di vedere il tuo schermo per una migliore assistenza',
    'od.secureConnection': 'Connessione sicura',
    'od.endToEndEncrypted': 'Crittografato end-to-end',
    'od.customerNotified': 'Il cliente sarà avvisato',
    'od.transferCall': 'Trasferisci chiamata',
    'od.transferCallDesc': 'Seleziona dove trasferire la chiamata attuale',
    'od.transferTo': 'Trasferisci a',
    'od.selectTransferDest': 'Seleziona la destinazione del trasferimento',
    'od.destManager': 'Manager',
    'od.destBilling': 'Reparto fatturazione',
    'od.destSales': 'Team vendite',
    'od.destSupervisor': 'Supervisore',
    'od.adjustVolume': 'Regola volume chiamata',
    'od.adjustVolumeDesc': 'Regola il livello del volume della chiamata attuale',
    'od.volumeLevel': 'Livello volume: {value}%',
    'od.saveResolveTicket': 'Salva e risolvi ticket',
    'od.saveResolveDesc': 'Aggiungi note di risoluzione e contrassegna questo ticket come risolto',
    'od.resolutionNotesLabel': 'Note di risoluzione *',
    'od.resolutionPlaceholder': 'Descrivi come è stato risolto il problema...',
    'od.resolveInfo': 'Questo contrassegnerà il ticket come risolto e chiuderà l\'interazione attuale.',
    'od.escalateTicket': 'Inoltra ticket',
    'od.escalateTicketDesc': 'Inoltra questo ticket a un supervisore o specialista',
    'od.escalationReasonLabel': 'Motivo dell\'inoltro *',
    'od.escalationPlaceholder': 'Spiega perché questo ticket deve essere inoltrato...',
    'od.escalateTo': 'Inoltra a',
    'od.escAdmin': 'Amministratore',
    'od.escalateInfo': 'Il ticket sarà trasferito al team selezionato per ulteriore assistenza.',
    'od.noIncomingCall': 'Nessuna chiamata in arrivo',
    'od.noIncomingCallDesc': 'Non c\'è nessuna chiamata in arrivo a cui rispondere.',
    'od.callOnHold': 'Chiamata in attesa',
    'od.callOnHoldDesc': 'Microfono disattivato. Il cliente è in attesa.',
    'od.callResumed': 'Chiamata ripresa',
    'od.callResumedDesc': 'Microfono attivo. Il cliente può sentirti.',
    'od.callDisconnected': 'La chiamata è stata disconnessa.',
    'od.transferTargetRequired': 'Destinazione del trasferimento richiesta',
    'od.transferTargetRequiredDesc': 'Seleziona un membro del personale a cui trasferire.',
    'od.noActiveCall': 'Nessuna chiamata attiva',
    'od.noActiveCallTransfer': 'Non c\'è nessuna chiamata WebRTC attiva da trasferire.',
    'od.callTransferred': 'Chiamata trasferita',
    'od.callTransferredDesc': 'Chiamata trasferita al membro del personale {target}.',
    'od.transferFailed': 'Trasferimento non riuscito',
    'od.noSolutionText': 'Nessun testo di soluzione',
    'od.noSolutionTextDesc': 'Questa soluzione non ha ancora contenuto inseribile.',
    'od.solutionInserted': 'Soluzione inserita',
    'od.solutionInsertedDesc': '"{title}" aggiunta alla tua risposta.',
    'od.solution': 'Soluzione',
    'od.fillTitleDescription': 'Compila il titolo e la descrizione',
    'od.ticketCreated': 'Ticket creato',
    'od.ticketCreatedDesc': 'Conversazione {id} creata.',
    'od.createFailed': 'Creazione non riuscita',
    'od.fillCustomerDateTime': 'Compila cliente, data e ora',
    'od.callbackScheduled': 'Richiamata pianificata',
    'od.callbackScheduledDesc': 'Richiamata per {customer} salvata.',
    'od.callbackFailed': 'Richiamata non riuscita',
    'od.reportDownloaded': 'Report scaricato',
    'od.reportDownloadedDesc': 'Report generato da dati in tempo reale.',
    'od.reportFailed': 'Report non riuscito',
    'od.screenShareRequiresCall': 'La condivisione dello schermo richiede una chiamata WebRTC attiva con il cliente.',
    'od.screenShareActive': 'Condivisione schermo attiva',
    'od.screenShareActiveDesc': 'Il tuo schermo è ora visibile al cliente.',
    'od.screenShareFailed': 'Condivisione schermo non riuscita',
    'od.screenShareFailedFallback': 'Impossibile avviare la condivisione dello schermo.',
    'od.resolutionNotesRequired': 'Note di risoluzione richieste',
    'od.resolutionNotesRequiredDesc': 'Fornisci note di risoluzione prima di salvare.',
    'od.noActiveTicket': 'Nessun ticket attivo',
    'od.selectTicketResolve': 'Seleziona un ticket prima di risolvere.',
    'od.ticketResolved': 'Ticket risolto',
    'od.ticketResolvedDesc': 'Ticket {id} risolto e salvato.',
    'od.resolutionFailed': 'Risoluzione non riuscita',
    'od.escalationReasonRequired': 'Motivo dell\'inoltro richiesto',
    'od.escalationReasonRequiredDesc': 'Fornisci un motivo per l\'inoltro.',
    'od.selectTicketEscalate': 'Seleziona un ticket prima di inoltrare.',
    'od.escalationCreatedDesc': 'Inoltro {id} creato.',
    'od.escalationFailed': 'Inoltro non riuscito',
    'od.noConversationLinked': 'Nessuna conversazione collegata',
    'od.noConversationLinkedDesc': 'Questo inoltro non ha una conversazione collegata. Impossibile inviare il messaggio.',
    'od.messageSent': 'Messaggio inviato',
    'od.messageSentDesc': 'Il cliente riceverà questo messaggio.',
    'od.sendFailed': 'Invio non riuscito',
    'od.subtitleVoiceSimulator': 'Testa l\'agente vocale IA con scenari complessi, query multilingue e contesti emotivi',
    'od.crmAuthReady': 'Autenticazione CRM pronta',
    'od.calling': 'Chiamata:',
    'od.selectScenarioVoice': 'Seleziona uno scenario per testare le capacità dell\'IA',
    'od.selectLanguage': 'Seleziona la lingua',
    'od.langEnglish': 'Inglese',
    'od.langFrench': 'Francese',
    'od.langMixed': 'Misto',
    'od.initiateCall': 'Avvia chiamata',
    'od.endCall': 'Termina chiamata',
    'od.processing': 'Elaborazione',
    'od.active': 'Attivo',
    'od.idle': 'Inattivo',
    'od.initiateCallToStart': 'Avvia una chiamata per iniziare i test',
    'od.listening': 'In ascolto...',
    'od.clickToSpeak': 'Fai clic per pronunciare un messaggio',
    'od.turnOffVoiceMode': 'Disattiva la modalità vocale continua',
    'od.turnOnVoiceMode': 'Attiva la modalità vocale continua',
    'od.aiVoice': 'Voce IA',
    'od.on': 'ON',
    'od.off': 'OFF',
    'od.thinking': 'Sto pensando',
    'od.voiceInputPlaceholder': 'Digita un messaggio e premi Invio (Maiusc+Invio per andare a capo), oppure usa i pulsanti del microfono sopra...',
    'od.vsReady': 'Pronto',
    'od.vsProcessing': 'Elaborazione...',
    'od.vsSpeaking': 'Sta parlando...',
    'od.vsError': 'Errore microfono',
    'od.notSupported': 'Non supportato',
    'od.speechRequires': 'Il riconoscimento vocale richiede Chrome, Edge o Safari 14.1+.',
    'od.micBlocked': 'Microfono bloccato',
    'od.micBlockedDesc': 'Consenti l\'accesso al microfono nelle impostazioni del browser e riprova.',
    'od.callInitiated': 'Chiamata avviata',
    'od.aiVoiceConnected': 'Agente vocale IA connesso. Sessione: {session}',
    'od.callFailed': 'Chiamata non riuscita',
    'od.callFailedDesc': 'Impossibile avviare la chiamata IA. Riprova.',
    'od.callEscalated': 'Chiamata inoltrata',
    'od.aiVoiceTerminated': 'Sessione vocale IA terminata.',
    'od.chatSimSubtitle': 'Simulatore di test QA — invia i messaggi digitati direttamente al backend di chat IA in tempo reale. Non è una vera sessione cliente. Testa l\'agente di chat IA con oltre 30 scenari, incluse situazioni complesse, query multilingue e contesti emotivi.',
    'od.qaSimulatorMode': 'Modalità simulatore QA',
    'od.userLabel': 'Utente:',
    'od.guest': 'Ospite',
    'od.crmAuth': 'Autenticazione CRM',
    'od.restart': 'Riavvia',
    'od.connected': 'Connesso',
    'od.aiActive': 'IA attiva',
    'od.realTime': 'Tempo reale',
    'od.voiceReady': 'Voce pronta',
    'od.listeningShort': 'In ascolto',
    'od.chattingWith': 'Chat con:',
    'od.selectScenarioChat': 'Seleziona uno scenario per testare le capacità della chat IA',
    'od.chatControls': 'Controlli chat',
    'od.customerPhone': 'Telefono del cliente',
    'od.startChat': 'Avvia chat',
    'od.endChat': 'Termina chat',
    'od.startChatToTest': 'Avvia una sessione di chat per iniziare i test',
    'od.chatInputPlaceholder': 'Digita il tuo messaggio o seleziona uno scenario...',
    'od.clear': 'Cancella',
    'od.chatInitiated': 'Chat avviata',
    'od.aiChatConnected': 'Agente di chat IA connesso. Sessione: {session}',
    'od.chatFailed': 'Chat non riuscita',
    'od.chatFailedDesc': 'Impossibile avviare la chat IA. Riprova.',
    'od.chatEscalated': 'Chat inoltrata',
    'od.chatEnded': 'Chat terminata',
    'od.aiChatTerminated': 'Sessione di chat IA terminata.',
  },
  ja: {
    'od.enableSound': 'サウンドを有効化',
    // ── AI-Voice human handoff (Phase C): media state + transcript/summary ──
    'od.mediaJoining': 'お客様におつなぎしています…',
    'od.waitingForCustomerOffer': 'お客様の接続を待っています…',
    'od.mediaConnecting': 'ライブ音声を確立しています…',
    'od.humanCallActive': '通話中 — ライブ音声が接続されました',
    'od.mediaFailed': 'ライブ音声を確立できませんでした。お客様に通知しました。',
    'od.aiContext': 'AIコンテキスト',
    'od.liveTranscript': 'ライブ文字起こし',
    'od.callSummary': '通話の要約',
    'od.actionItems': 'アクション項目',
    'od.commitments': '約束事項',
    'od.speakerAgent': 'オペレーター',
    'od.speakerCustomer': 'お客様',
    'od.networkError': 'ネットワークエラー',
    'od.missingInformation': '情報が不足しています',
    'od.customer': '顧客',
    'od.aiAgent': 'AIエージェント',
    'od.escalated': 'エスカレーション済み',
    'od.sendMessage': 'メッセージを送信',
    'od.testScenarios': 'テストシナリオ',
    'od.selectedScenario': '選択したシナリオ',
    'od.expected': '想定:',
    'od.conversation': '会話',
    'od.sessionLabel': 'セッション参照:',
    'od.languageLabel': '言語:',
    'od.intentLabel': '意図:',
    'od.detecting': '検出中...',
    'od.testEmailOverride': 'テスト用メール（上書き）',
    'od.optionalSimulatorOnly': '（任意 — シミュレーターのみ）',
    'od.emailOverridePlaceholder': '例: you@example.com — 電話番号検索をバイパス',
    'od.customerName': '顧客名',
    'od.escalatingTo': '{target} にエスカレーション中: {reason}',
    'od.processingFailed': '処理に失敗しました',
    'od.processFailedFallback': 'メッセージを処理できませんでした。もう一度お試しください。',
    'od.callEnded': '通話終了',
    'od.tabBasic': '基本',
    'od.tabComplex': '複雑',
    'od.tabAmbiguous': '曖昧',
    'od.tabMultilingual': '多言語',
    'od.tabEmpathy': '共感',
    'od.tabEnvironment': '環境',
    'od.tabContextual': '文脈',
    'od.subtitleUnifiedDesktop': 'すべてのチャネル、顧客データ、ツールを1つのインターフェースに',
    'od.takeBreak': '休憩する',
    'od.endBreak': '休憩を終了',
    'od.callsToday': '本日の通話',
    'od.avgHandleTime': '平均処理時間',
    'od.target': '目標: {value}',
    'od.firstCallResolution': '初回解決率',
    'od.customerSatisfaction': '顧客満足度',
    'od.ticketsResolved': '解決済みチケット',
    'od.performanceExcellent': 'パフォーマンス: 優秀',
    'od.channelOverview': 'チャネル概要',
    'od.channelVoice': '音声',
    'od.channelChat': 'チャット',
    'od.channelEmail': 'メール',
    'od.channelSocial': 'ソーシャル',
    'od.callControls': '通話コントロール',
    'od.answer': '応答',
    'od.incomingHandoff': '着信ハンドオフ',
    'od.notEligible': 'このハンドオフには応答できません',
    'od.assignedOther': '別のオペレーターに割り当て済み',
    'od.noLongerAvailable': '利用できなくなりました',
    'od.handoffExpired': 'ハンドオフの有効期限が切れました',
    'od.alreadyAcceptedOther': '別のオペレーターが既に受け付けました',
    'od.handoffLocked': 'このハンドオフはお使いのデスクからは操作できません',
    'od.onHold': '保留中',
    'od.live': '通話中',
    'od.connectedNoAudio': '接続済み — ライブ音声なし',
    'od.handoffChannel': '{channel}ハンドオフ',
    'od.sourceAiVoice': 'AI音声',
    'od.sourceAiChat': 'AIチャット',
    'od.handoffToast': '顧客ハンドオフの着信',
    'od.handoffToastUrgent': '緊急の顧客ハンドオフ',
    'od.opCallIncoming': 'オペレーター着信',
    'od.opCallIncomingVideo': 'ビデオ通話の着信',
    'od.opCallIncomingVoice': '音声通話の着信',
    'od.opCallCustomer': '顧客',
    'od.opCallDecline': '拒否',
    'od.opCallStarting': '開始中…',
    'od.opCallAnswered': '応答しました',
    'od.opCallConnecting': '接続中 — 発信者の音声を待機中',
    'od.opCallAnsweredElsewhere': '別の端末で応答済み',
    'od.opCallAnsweredElsewhereDesc': 'この通話は別の端末で応答されました。',
    'od.opCallFailed': '応答できませんでした',
    'od.opCallNoConnection': '接続がありません。再接続してからもう一度お試しください。',
    'od.hangUp': '切断',
    'od.resume': '再開',
    'od.hold': '保留',
    'od.mute': 'ミュート',
    'od.unmute': 'ミュート解除',
    'od.volume': '音量',
    'od.customerInteraction': '顧客対応 - {channel}',
    'od.liveCall': 'ライブ通話',
    'od.issueLabel': '問題:',
    'od.prioritySuffix': '優先度 {priority}',
    'od.customerHistory': '顧客履歴',
    'od.resolved': '解決済み',
    'od.pending': '保留中',
    'od.callNotes': '通話メモ',
    'od.addNotesPlaceholder': 'この通話に関するメモを追加...',
    'od.quickResponsePlaceholder': 'クイック返信...',
    'od.handlingAs': '{name} として対応中',
    'od.callQualityScore': '通話品質スコア',
    'od.micMuted': 'マイクがミュートされています',
    'od.saveResolve': '保存して解決',
    'od.escalate': 'エスカレーション',
    'od.transfer': '転送',
    'od.readyNext': '次の対応の準備完了',
    'od.waitingIncoming': '{channel} の着信対応を待機中',
    'od.queueWaiting': 'キュー: {count} 件待機中',
    'od.tabKB': 'KB',
    'od.tabQuick': 'クイック',
    'od.tabTools': 'ツール',
    'od.knowledgeBase': 'ナレッジベース',
    'od.searchSolutions': 'ソリューションを検索...',
    'od.noApprovedSolutions': '承認済みソリューションはまだありません。',
    'od.noSolutionsMatch': '検索に一致するソリューションはありません。',
    'od.clickToInsert': 'クリックしてこのソリューションを返信に挿入',
    'od.usesSuffix': '{count} 回使用',
    'od.quickResponses': 'クイック返信',
    'od.productivityTools': '生産性ツール',
    'od.createTicket': 'チケットを作成',
    'od.createNewTicket': '新規チケットを作成',
    'od.createTicketDesc': '現在の顧客の問題に対するサポートチケットを作成します',
    'od.titleRequired': 'タイトル *',
    'od.descriptionRequired': '説明 *',
    'od.priority': '優先度',
    'od.category': 'カテゴリー',
    'od.customerNameEmailPlaceholder': '顧客名またはメール',
    'od.briefIssuePlaceholder': '問題の簡単な説明',
    'od.detailedIssuePlaceholder': '問題の詳細な説明',
    'od.priorityLow': '低',
    'od.priorityMedium': '中',
    'od.priorityHigh': '高',
    'od.priorityUrgent': '緊急',
    'od.catTechnical': 'テクニカルサポート',
    'od.catBilling': '請求',
    'od.catProduct': '製品の問題',
    'od.catAccount': 'アカウント管理',
    'od.scheduleCallback': 'コールバックを予約',
    'od.scheduleCallbackDesc': '顧客のためにコールバックを予約します',
    'od.customerNameRequired': '顧客名 *',
    'od.phoneNumber': '電話番号',
    'od.customerNamePlaceholder': '顧客名',
    'od.phonePlaceholder': '+1-555-0123',
    'od.dateRequired': '日付 *',
    'od.timeRequired': '時刻 *',
    'od.notes': 'メモ',
    'od.callbackNotesPlaceholder': 'コールバックの追加メモ',
    'od.generateReport': 'レポートを生成',
    'od.generateReportDesc': 'パフォーマンスまたはアクティビティレポートを生成します',
    'od.reportType': 'レポートタイプ',
    'od.timePeriod': '期間',
    'od.format': '形式',
    'od.reportPerformance': 'パフォーマンスレポート',
    'od.reportActivity': 'アクティビティレポート',
    'od.reportCustomer': '顧客対応',
    'od.reportResolution': '解決サマリー',
    'od.periodToday': '今日',
    'od.periodWeek': '今週',
    'od.periodMonth': '今月',
    'od.periodQuarter': '今四半期',
    'od.screenShare': '画面共有',
    'od.screenShareDesc': '顧客と画面共有セッションを開始します',
    'od.startScreenShare': '画面共有を開始',
    'od.screenShareInfo': 'これにより、より良いサポートのために顧客があなたの画面を見ることができます',
    'od.secureConnection': '安全な接続',
    'od.endToEndEncrypted': 'エンドツーエンド暗号化',
    'od.customerNotified': '顧客に通知されます',
    'od.transferCall': '通話を転送',
    'od.transferCallDesc': '現在の通話の転送先を選択してください',
    'od.transferTo': '転送先',
    'od.selectTransferDest': '転送先を選択',
    'od.destManager': 'マネージャー',
    'od.destBilling': '請求部門',
    'od.destSales': '営業チーム',
    'od.destSupervisor': 'スーパーバイザー',
    'od.adjustVolume': '通話音量を調整',
    'od.adjustVolumeDesc': '現在の通話の音量レベルを調整します',
    'od.volumeLevel': '音量レベル: {value}%',
    'od.saveResolveTicket': 'チケットを保存して解決',
    'od.saveResolveDesc': '解決メモを追加し、このチケットを解決済みとしてマークします',
    'od.resolutionNotesLabel': '解決メモ *',
    'od.resolutionPlaceholder': '問題がどのように解決されたかを記述...',
    'od.resolveInfo': 'これによりチケットが解決済みとしてマークされ、現在の対応が終了します。',
    'od.escalateTicket': 'チケットをエスカレーション',
    'od.escalateTicketDesc': 'このチケットをスーパーバイザーまたは専門家にエスカレーションします',
    'od.escalationReasonLabel': 'エスカレーション理由 *',
    'od.escalationPlaceholder': 'このチケットをエスカレーションする必要がある理由を説明...',
    'od.escalateTo': 'エスカレーション先',
    'od.escAdmin': '管理者',
    'od.escalateInfo': 'チケットはさらなるサポートのために選択したチームに転送されます。',
    'od.noIncomingCall': '着信なし',
    'od.noIncomingCallDesc': '応答する着信がありません。',
    'od.callOnHold': '通話保留中',
    'od.callOnHoldDesc': 'マイクがミュートされました。顧客は保留中です。',
    'od.callResumed': '通話再開',
    'od.callResumedDesc': 'マイクが有効です。顧客はあなたの声を聞くことができます。',
    'od.callDisconnected': '通話が切断されました。',
    'od.transferTargetRequired': '転送先が必要です',
    'od.transferTargetRequiredDesc': '転送先のスタッフを選択してください。',
    'od.noActiveCall': 'アクティブな通話なし',
    'od.noActiveCallTransfer': '転送するアクティブなWebRTC通話がありません。',
    'od.callTransferred': '通話転送済み',
    'od.callTransferredDesc': '通話をスタッフ {target} に転送しました。',
    'od.transferFailed': '転送に失敗しました',
    'od.noSolutionText': 'ソリューションテキストなし',
    'od.noSolutionTextDesc': 'このソリューションにはまだ挿入可能なコンテンツがありません。',
    'od.solutionInserted': 'ソリューションを挿入しました',
    'od.solutionInsertedDesc': '「{title}」を返信に追加しました。',
    'od.solution': 'ソリューション',
    'od.fillTitleDescription': 'タイトルと説明を入力してください',
    'od.ticketCreated': 'チケットを作成しました',
    'od.ticketCreatedDesc': '会話 {id} を作成しました。',
    'od.createFailed': '作成に失敗しました',
    'od.fillCustomerDateTime': '顧客、日付、時刻を入力してください',
    'od.callbackScheduled': 'コールバックを予約しました',
    'od.callbackScheduledDesc': '{customer} のコールバックを保存しました。',
    'od.callbackFailed': 'コールバックに失敗しました',
    'od.reportDownloaded': 'レポートをダウンロードしました',
    'od.reportDownloadedDesc': 'ライブデータからレポートを生成しました。',
    'od.reportFailed': 'レポートに失敗しました',
    'od.screenShareRequiresCall': '画面共有には顧客とのアクティブなWebRTC通話が必要です。',
    'od.screenShareActive': '画面共有がアクティブ',
    'od.screenShareActiveDesc': 'あなたの画面は顧客に表示されています。',
    'od.screenShareFailed': '画面共有に失敗しました',
    'od.screenShareFailedFallback': '画面共有を開始できませんでした。',
    'od.resolutionNotesRequired': '解決メモが必要です',
    'od.resolutionNotesRequiredDesc': '保存する前に解決メモを入力してください。',
    'od.noActiveTicket': 'アクティブなチケットなし',
    'od.selectTicketResolve': '解決する前にチケットを選択してください。',
    'od.ticketResolved': 'チケットを解決しました',
    'od.ticketResolvedDesc': 'チケット {id} を解決して保存しました。',
    'od.resolutionFailed': '解決に失敗しました',
    'od.escalationReasonRequired': 'エスカレーション理由が必要です',
    'od.escalationReasonRequiredDesc': 'エスカレーションの理由を入力してください。',
    'od.selectTicketEscalate': 'エスカレーションする前にチケットを選択してください。',
    'od.escalationCreatedDesc': 'エスカレーション {id} を作成しました。',
    'od.escalationFailed': 'エスカレーションに失敗しました',
    'od.noConversationLinked': 'リンクされた会話なし',
    'od.noConversationLinkedDesc': 'このエスカレーションにはリンクされた会話がありません。メッセージを送信できません。',
    'od.messageSent': 'メッセージを送信しました',
    'od.messageSentDesc': '顧客はこのメッセージを受け取ります。',
    'od.sendFailed': '送信に失敗しました',
    'od.subtitleVoiceSimulator': '複雑なシナリオ、多言語クエリ、感情的な文脈でAI音声エージェントをテスト',
    'od.crmAuthReady': 'CRM認証の準備完了',
    'od.calling': '発信中:',
    'od.selectScenarioVoice': 'シナリオを選択してAIの機能をテスト',
    'od.selectLanguage': '言語を選択',
    'od.langEnglish': '英語',
    'od.langFrench': 'フランス語',
    'od.langMixed': '混合',
    'od.initiateCall': '通話を開始',
    'od.endCall': '通話を終了',
    'od.processing': '処理中',
    'od.active': 'アクティブ',
    'od.idle': 'アイドル',
    'od.initiateCallToStart': '通話を開始してテストを始めてください',
    'od.listening': '聞き取り中...',
    'od.clickToSpeak': 'クリックして1つのメッセージを話す',
    'od.turnOffVoiceMode': '連続音声モードをオフにする',
    'od.turnOnVoiceMode': '連続音声モードをオンにする',
    'od.aiVoice': 'AI音声',
    'od.on': 'オン',
    'od.off': 'オフ',
    'od.thinking': '思考中',
    'od.voiceInputPlaceholder': 'メッセージを入力してEnterキーを押す（Shift+Enterで改行）か、上のマイクボタンを使用...',
    'od.vsReady': '準備完了',
    'od.vsProcessing': '処理中...',
    'od.vsSpeaking': '話し中...',
    'od.vsError': 'マイクエラー',
    'od.notSupported': 'サポートされていません',
    'od.speechRequires': '音声認識にはChrome、Edge、またはSafari 14.1以降が必要です。',
    'od.micBlocked': 'マイクがブロックされています',
    'od.micBlockedDesc': 'ブラウザの設定でマイクへのアクセスを許可して、もう一度お試しください。',
    'od.callInitiated': '通話を開始しました',
    'od.aiVoiceConnected': 'AI音声エージェントに接続しました。セッション: {session}',
    'od.callFailed': '通話に失敗しました',
    'od.callFailedDesc': 'AI通話を開始できませんでした。もう一度お試しください。',
    'od.callEscalated': '通話をエスカレーションしました',
    'od.aiVoiceTerminated': 'AI音声セッションを終了しました。',
    'od.chatSimSubtitle': 'QAテストシミュレーター — 入力したメッセージをライブAIチャットバックエンドに直接送信します。実際の顧客セッションではありません。複雑な状況、多言語クエリ、感情的な文脈を含む30以上のシナリオでAIチャットエージェントをテストします。',
    'od.qaSimulatorMode': 'QAシミュレーターモード',
    'od.userLabel': 'ユーザー:',
    'od.guest': 'ゲスト',
    'od.crmAuth': 'CRM認証',
    'od.restart': '再起動',
    'od.connected': '接続済み',
    'od.aiActive': 'AIアクティブ',
    'od.realTime': 'リアルタイム',
    'od.voiceReady': '音声準備完了',
    'od.listeningShort': '聞き取り中',
    'od.chattingWith': 'チャット相手:',
    'od.selectScenarioChat': 'シナリオを選択してAIチャットの機能をテスト',
    'od.chatControls': 'チャットコントロール',
    'od.customerPhone': '顧客の電話番号',
    'od.startChat': 'チャットを開始',
    'od.endChat': 'チャットを終了',
    'od.startChatToTest': 'チャットセッションを開始してテストを始めてください',
    'od.chatInputPlaceholder': 'メッセージを入力するかシナリオを選択...',
    'od.clear': 'クリア',
    'od.chatInitiated': 'チャットを開始しました',
    'od.aiChatConnected': 'AIチャットエージェントに接続しました。セッション: {session}',
    'od.chatFailed': 'チャットに失敗しました',
    'od.chatFailedDesc': 'AIチャットを開始できませんでした。もう一度お試しください。',
    'od.chatEscalated': 'チャットをエスカレーションしました',
    'od.chatEnded': 'チャット終了',
    'od.aiChatTerminated': 'AIチャットセッションを終了しました。',
  },
  ko: {
    'od.enableSound': '소리 켜기',
    // ── AI-Voice human handoff (Phase C): media state + transcript/summary ──
    'od.mediaJoining': '고객과 연결 중입니다…',
    'od.waitingForCustomerOffer': '고객의 연결을 기다리는 중…',
    'od.mediaConnecting': '실시간 오디오 설정 중…',
    'od.humanCallActive': '상담원 통화 중 — 실시간 오디오 연결됨',
    'od.mediaFailed': '실시간 오디오를 설정할 수 없습니다. 고객에게 통지되었습니다.',
    'od.aiContext': 'AI 컨텍스트',
    'od.liveTranscript': '실시간 전사',
    'od.callSummary': '통화 요약',
    'od.actionItems': '조치 항목',
    'od.commitments': '약속 사항',
    'od.speakerAgent': '상담원',
    'od.speakerCustomer': '고객',
    'od.networkError': '네트워크 오류',
    'od.missingInformation': '정보 누락',
    'od.customer': '고객',
    'od.aiAgent': 'AI 상담원',
    'od.escalated': '에스컬레이션됨',
    'od.sendMessage': '메시지 보내기',
    'od.testScenarios': '테스트 시나리오',
    'od.selectedScenario': '선택한 시나리오',
    'od.expected': '예상:',
    'od.conversation': '대화',
    'od.sessionLabel': '세션 참조:',
    'od.languageLabel': '언어:',
    'od.intentLabel': '의도:',
    'od.detecting': '감지 중...',
    'od.testEmailOverride': '테스트 이메일(재정의)',
    'od.optionalSimulatorOnly': '(선택 사항 — 시뮬레이터 전용)',
    'od.emailOverridePlaceholder': '예: you@example.com — 전화번호 조회 우회',
    'od.customerName': '고객 이름',
    'od.escalatingTo': '{target}(으)로 에스컬레이션 중: {reason}',
    'od.processingFailed': '처리 실패',
    'od.processFailedFallback': '메시지를 처리할 수 없습니다. 다시 시도해 주세요.',
    'od.callEnded': '통화 종료',
    'od.tabBasic': '기본',
    'od.tabComplex': '복잡',
    'od.tabAmbiguous': '모호',
    'od.tabMultilingual': '다국어',
    'od.tabEmpathy': '공감',
    'od.tabEnvironment': '환경',
    'od.tabContextual': '맥락',
    'od.subtitleUnifiedDesktop': '모든 채널, 고객 데이터 및 도구를 하나의 인터페이스에서',
    'od.takeBreak': '휴식하기',
    'od.endBreak': '휴식 종료',
    'od.callsToday': '오늘 통화',
    'od.avgHandleTime': '평균 처리 시간',
    'od.target': '목표: {value}',
    'od.firstCallResolution': '첫 통화 해결률',
    'od.customerSatisfaction': '고객 만족도',
    'od.ticketsResolved': '해결된 티켓',
    'od.performanceExcellent': '성과: 우수',
    'od.channelOverview': '채널 개요',
    'od.channelVoice': '음성',
    'od.channelChat': '채팅',
    'od.channelEmail': '이메일',
    'od.channelSocial': '소셜',
    'od.callControls': '통화 제어',
    'od.answer': '응답',
    'od.incomingHandoff': '수신 핸드오프',
    'od.notEligible': '이 핸드오프에 응답할 수 없습니다',
    'od.assignedOther': '다른 상담원에게 배정됨',
    'od.noLongerAvailable': '더 이상 사용할 수 없습니다',
    'od.handoffExpired': '핸드오프가 만료되었습니다',
    'od.alreadyAcceptedOther': '다른 상담원이 이미 수락했습니다',
    'od.handoffLocked': '이 핸드오프는 사용자의 데스크에서 처리할 수 없습니다',
    'od.onHold': '대기 중',
    'od.live': '실시간',
    'od.connectedNoAudio': '연결됨 — 실시간 오디오 없음',
    'od.handoffChannel': '{channel} 핸드오프',
    'od.sourceAiVoice': 'AI 음성',
    'od.sourceAiChat': 'AI 채팅',
    'od.handoffToast': '고객 핸드오프 수신',
    'od.handoffToastUrgent': '긴급 고객 핸드오프',
    'od.opCallIncoming': '운영자 수신 전화',
    'od.opCallIncomingVideo': '영상 통화 수신',
    'od.opCallIncomingVoice': '음성 통화 수신',
    'od.opCallCustomer': '고객',
    'od.opCallDecline': '거절',
    'od.opCallStarting': '시작 중…',
    'od.opCallAnswered': '전화 응답됨',
    'od.opCallConnecting': '연결 중 — 발신자 오디오 대기 중',
    'od.opCallAnsweredElsewhere': '다른 기기에서 응답함',
    'od.opCallAnsweredElsewhereDesc': '이 통화는 다른 기기에서 응답되었습니다.',
    'od.opCallFailed': '응답할 수 없습니다',
    'od.opCallNoConnection': '연결이 없습니다. 다시 연결한 후 시도하세요.',
    'od.hangUp': '끊기',
    'od.resume': '재개',
    'od.hold': '보류',
    'od.mute': '음소거',
    'od.unmute': '음소거 해제',
    'od.volume': '볼륨',
    'od.customerInteraction': '고객 상호작용 - {channel}',
    'od.liveCall': '실시간 통화',
    'od.issueLabel': '문제:',
    'od.prioritySuffix': '{priority} 우선순위',
    'od.customerHistory': '고객 이력',
    'od.resolved': '해결됨',
    'od.pending': '대기 중',
    'od.callNotes': '통화 메모',
    'od.addNotesPlaceholder': '이 통화에 대한 메모 추가...',
    'od.quickResponsePlaceholder': '빠른 응답...',
    'od.handlingAs': '{name}(으)로 처리 중',
    'od.callQualityScore': '통화 품질 점수',
    'od.micMuted': '마이크가 음소거되었습니다',
    'od.saveResolve': '저장 및 해결',
    'od.escalate': '에스컬레이션',
    'od.transfer': '전환',
    'od.readyNext': '다음 상호작용 준비 완료',
    'od.waitingIncoming': '수신 {channel} 상호작용 대기 중',
    'od.queueWaiting': '대기열: {count}건 대기 중',
    'od.tabKB': 'KB',
    'od.tabQuick': '빠른',
    'od.tabTools': '도구',
    'od.knowledgeBase': '지식 베이스',
    'od.searchSolutions': '솔루션 검색...',
    'od.noApprovedSolutions': '아직 승인된 솔루션이 없습니다.',
    'od.noSolutionsMatch': '검색과 일치하는 솔루션이 없습니다.',
    'od.clickToInsert': '클릭하여 이 솔루션을 답변에 삽입',
    'od.usesSuffix': '{count}회 사용',
    'od.quickResponses': '빠른 응답',
    'od.productivityTools': '생산성 도구',
    'od.createTicket': '티켓 생성',
    'od.createNewTicket': '새 티켓 생성',
    'od.createTicketDesc': '현재 고객 문제에 대한 지원 티켓을 생성합니다',
    'od.titleRequired': '제목 *',
    'od.descriptionRequired': '설명 *',
    'od.priority': '우선순위',
    'od.category': '카테고리',
    'od.customerNameEmailPlaceholder': '고객 이름 또는 이메일',
    'od.briefIssuePlaceholder': '문제에 대한 간단한 설명',
    'od.detailedIssuePlaceholder': '문제에 대한 자세한 설명',
    'od.priorityLow': '낮음',
    'od.priorityMedium': '보통',
    'od.priorityHigh': '높음',
    'od.priorityUrgent': '긴급',
    'od.catTechnical': '기술 지원',
    'od.catBilling': '청구',
    'od.catProduct': '제품 문제',
    'od.catAccount': '계정 관리',
    'od.scheduleCallback': '콜백 예약',
    'od.scheduleCallbackDesc': '고객을 위한 콜백을 예약합니다',
    'od.customerNameRequired': '고객 이름 *',
    'od.phoneNumber': '전화번호',
    'od.customerNamePlaceholder': '고객 이름',
    'od.phonePlaceholder': '+1-555-0123',
    'od.dateRequired': '날짜 *',
    'od.timeRequired': '시간 *',
    'od.notes': '메모',
    'od.callbackNotesPlaceholder': '콜백을 위한 추가 메모',
    'od.generateReport': '보고서 생성',
    'od.generateReportDesc': '성과 또는 활동 보고서를 생성합니다',
    'od.reportType': '보고서 유형',
    'od.timePeriod': '기간',
    'od.format': '형식',
    'od.reportPerformance': '성과 보고서',
    'od.reportActivity': '활동 보고서',
    'od.reportCustomer': '고객 상호작용',
    'od.reportResolution': '해결 요약',
    'od.periodToday': '오늘',
    'od.periodWeek': '이번 주',
    'od.periodMonth': '이번 달',
    'od.periodQuarter': '이번 분기',
    'od.screenShare': '화면 공유',
    'od.screenShareDesc': '고객과 화면 공유 세션을 시작합니다',
    'od.startScreenShare': '화면 공유 시작',
    'od.screenShareInfo': '이렇게 하면 더 나은 지원을 위해 고객이 화면을 볼 수 있습니다',
    'od.secureConnection': '보안 연결',
    'od.endToEndEncrypted': '종단 간 암호화',
    'od.customerNotified': '고객에게 알림이 전송됩니다',
    'od.transferCall': '통화 전환',
    'od.transferCallDesc': '현재 통화를 전환할 대상을 선택하세요',
    'od.transferTo': '전환 대상',
    'od.selectTransferDest': '전환 대상 선택',
    'od.destManager': '매니저',
    'od.destBilling': '청구 부서',
    'od.destSales': '영업팀',
    'od.destSupervisor': '감독관',
    'od.adjustVolume': '통화 볼륨 조정',
    'od.adjustVolumeDesc': '현재 통화의 볼륨 수준을 조정합니다',
    'od.volumeLevel': '볼륨 수준: {value}%',
    'od.saveResolveTicket': '티켓 저장 및 해결',
    'od.saveResolveDesc': '해결 메모를 추가하고 이 티켓을 해결됨으로 표시합니다',
    'od.resolutionNotesLabel': '해결 메모 *',
    'od.resolutionPlaceholder': '문제가 어떻게 해결되었는지 설명...',
    'od.resolveInfo': '이렇게 하면 티켓이 해결됨으로 표시되고 현재 상호작용이 종료됩니다.',
    'od.escalateTicket': '티켓 에스컬레이션',
    'od.escalateTicketDesc': '이 티켓을 감독관이나 전문가에게 에스컬레이션합니다',
    'od.escalationReasonLabel': '에스컬레이션 사유 *',
    'od.escalationPlaceholder': '이 티켓을 에스컬레이션해야 하는 이유를 설명...',
    'od.escalateTo': '에스컬레이션 대상',
    'od.escAdmin': '관리자',
    'od.escalateInfo': '티켓은 추가 지원을 위해 선택한 팀으로 전환됩니다.',
    'od.noIncomingCall': '수신 통화 없음',
    'od.noIncomingCallDesc': '응답할 수신 통화가 없습니다.',
    'od.callOnHold': '통화 보류',
    'od.callOnHoldDesc': '마이크가 음소거되었습니다. 고객이 보류 중입니다.',
    'od.callResumed': '통화 재개',
    'od.callResumedDesc': '마이크가 활성화되었습니다. 고객이 들을 수 있습니다.',
    'od.callDisconnected': '통화가 끊겼습니다.',
    'od.transferTargetRequired': '전환 대상 필요',
    'od.transferTargetRequiredDesc': '전환할 직원을 선택하세요.',
    'od.noActiveCall': '활성 통화 없음',
    'od.noActiveCallTransfer': '전환할 활성 WebRTC 통화가 없습니다.',
    'od.callTransferred': '통화 전환됨',
    'od.callTransferredDesc': '직원 {target}(으)로 통화를 전환했습니다.',
    'od.transferFailed': '전환 실패',
    'od.noSolutionText': '솔루션 텍스트 없음',
    'od.noSolutionTextDesc': '이 솔루션에는 아직 삽입 가능한 콘텐츠가 없습니다.',
    'od.solutionInserted': '솔루션 삽입됨',
    'od.solutionInsertedDesc': '"{title}"을(를) 답변에 추가했습니다.',
    'od.solution': '솔루션',
    'od.fillTitleDescription': '제목과 설명을 입력하세요',
    'od.ticketCreated': '티켓 생성됨',
    'od.ticketCreatedDesc': '대화 {id}이(가) 생성되었습니다.',
    'od.createFailed': '생성 실패',
    'od.fillCustomerDateTime': '고객, 날짜 및 시간을 입력하세요',
    'od.callbackScheduled': '콜백 예약됨',
    'od.callbackScheduledDesc': '{customer}의 콜백이 저장되었습니다.',
    'od.callbackFailed': '콜백 실패',
    'od.reportDownloaded': '보고서 다운로드됨',
    'od.reportDownloadedDesc': '실시간 데이터에서 보고서를 생성했습니다.',
    'od.reportFailed': '보고서 실패',
    'od.screenShareRequiresCall': '화면 공유에는 고객과의 활성 WebRTC 통화가 필요합니다.',
    'od.screenShareActive': '화면 공유 활성',
    'od.screenShareActiveDesc': '이제 고객이 화면을 볼 수 있습니다.',
    'od.screenShareFailed': '화면 공유 실패',
    'od.screenShareFailedFallback': '화면 공유를 시작할 수 없습니다.',
    'od.resolutionNotesRequired': '해결 메모 필요',
    'od.resolutionNotesRequiredDesc': '저장하기 전에 해결 메모를 입력하세요.',
    'od.noActiveTicket': '활성 티켓 없음',
    'od.selectTicketResolve': '해결하기 전에 티켓을 선택하세요.',
    'od.ticketResolved': '티켓 해결됨',
    'od.ticketResolvedDesc': '티켓 {id}이(가) 해결되고 저장되었습니다.',
    'od.resolutionFailed': '해결 실패',
    'od.escalationReasonRequired': '에스컬레이션 사유 필요',
    'od.escalationReasonRequiredDesc': '에스컬레이션 사유를 입력하세요.',
    'od.selectTicketEscalate': '에스컬레이션하기 전에 티켓을 선택하세요.',
    'od.escalationCreatedDesc': '에스컬레이션 {id}이(가) 생성되었습니다.',
    'od.escalationFailed': '에스컬레이션 실패',
    'od.noConversationLinked': '연결된 대화 없음',
    'od.noConversationLinkedDesc': '이 에스컬레이션에는 연결된 대화가 없습니다. 메시지를 보낼 수 없습니다.',
    'od.messageSent': '메시지 전송됨',
    'od.messageSentDesc': '고객이 이 메시지를 받게 됩니다.',
    'od.sendFailed': '전송 실패',
    'od.subtitleVoiceSimulator': '복잡한 시나리오, 다국어 쿼리 및 감정적 맥락으로 AI 음성 상담원 테스트',
    'od.crmAuthReady': 'CRM 인증 준비 완료',
    'od.calling': '전화 거는 중:',
    'od.selectScenarioVoice': '시나리오를 선택하여 AI 기능 테스트',
    'od.selectLanguage': '언어 선택',
    'od.langEnglish': '영어',
    'od.langFrench': '프랑스어',
    'od.langMixed': '혼합',
    'od.initiateCall': '통화 시작',
    'od.endCall': '통화 종료',
    'od.processing': '처리 중',
    'od.active': '활성',
    'od.idle': '유휴',
    'od.initiateCallToStart': '통화를 시작하여 테스트를 시작하세요',
    'od.listening': '듣는 중...',
    'od.clickToSpeak': '클릭하여 메시지 하나 말하기',
    'od.turnOffVoiceMode': '연속 음성 모드 끄기',
    'od.turnOnVoiceMode': '연속 음성 모드 켜기',
    'od.aiVoice': 'AI 음성',
    'od.on': '켜짐',
    'od.off': '꺼짐',
    'od.thinking': '생각 중',
    'od.voiceInputPlaceholder': '메시지를 입력하고 Enter를 누르거나(줄 바꿈은 Shift+Enter), 위의 마이크 버튼을 사용하세요...',
    'od.vsReady': '준비 완료',
    'od.vsProcessing': '처리 중...',
    'od.vsSpeaking': '말하는 중...',
    'od.vsError': '마이크 오류',
    'od.notSupported': '지원되지 않음',
    'od.speechRequires': '음성 인식에는 Chrome, Edge 또는 Safari 14.1 이상이 필요합니다.',
    'od.micBlocked': '마이크 차단됨',
    'od.micBlockedDesc': '브라우저 설정에서 마이크 액세스를 허용하고 다시 시도하세요.',
    'od.callInitiated': '통화 시작됨',
    'od.aiVoiceConnected': 'AI 음성 상담원에 연결되었습니다. 세션: {session}',
    'od.callFailed': '통화 실패',
    'od.callFailedDesc': 'AI 통화를 시작할 수 없습니다. 다시 시도해 주세요.',
    'od.callEscalated': '통화 에스컬레이션됨',
    'od.aiVoiceTerminated': 'AI 음성 세션이 종료되었습니다.',
    'od.chatSimSubtitle': 'QA 테스트 시뮬레이터 — 입력한 메시지를 실시간 AI 채팅 백엔드로 직접 전송합니다. 실제 고객 세션이 아닙니다. 복잡한 상황, 다국어 쿼리 및 감정적 맥락을 포함한 30개 이상의 시나리오로 AI 채팅 상담원을 테스트합니다.',
    'od.qaSimulatorMode': 'QA 시뮬레이터 모드',
    'od.userLabel': '사용자:',
    'od.guest': '게스트',
    'od.crmAuth': 'CRM 인증',
    'od.restart': '다시 시작',
    'od.connected': '연결됨',
    'od.aiActive': 'AI 활성',
    'od.realTime': '실시간',
    'od.voiceReady': '음성 준비 완료',
    'od.listeningShort': '듣는 중',
    'od.chattingWith': '채팅 상대:',
    'od.selectScenarioChat': '시나리오를 선택하여 AI 채팅 기능 테스트',
    'od.chatControls': '채팅 제어',
    'od.customerPhone': '고객 전화번호',
    'od.startChat': '채팅 시작',
    'od.endChat': '채팅 종료',
    'od.startChatToTest': '채팅 세션을 시작하여 테스트를 시작하세요',
    'od.chatInputPlaceholder': '메시지를 입력하거나 시나리오를 선택하세요...',
    'od.clear': '지우기',
    'od.chatInitiated': '채팅 시작됨',
    'od.aiChatConnected': 'AI 채팅 상담원에 연결되었습니다. 세션: {session}',
    'od.chatFailed': '채팅 실패',
    'od.chatFailedDesc': 'AI 채팅을 시작할 수 없습니다. 다시 시도해 주세요.',
    'od.chatEscalated': '채팅 에스컬레이션됨',
    'od.chatEnded': '채팅 종료',
    'od.aiChatTerminated': 'AI 채팅 세션이 종료되었습니다.',
  },
  zh: {
    'od.enableSound': '启用声音',
    // ── AI-Voice human handoff (Phase C): media state + transcript/summary ──
    'od.mediaJoining': '正在为您接通客户…',
    'od.waitingForCustomerOffer': '正在等待客户接入…',
    'od.mediaConnecting': '正在建立实时音频…',
    'od.humanCallActive': '人工通话进行中 — 实时音频已连接',
    'od.mediaFailed': '无法建立实时音频。已通知客户。',
    'od.aiContext': 'AI 上下文',
    'od.liveTranscript': '实时转录',
    'od.callSummary': '通话摘要',
    'od.actionItems': '待办事项',
    'od.commitments': '承诺事项',
    'od.speakerAgent': '客服',
    'od.speakerCustomer': '客户',
    'od.networkError': '网络错误',
    'od.missingInformation': '信息缺失',
    'od.customer': '客户',
    'od.aiAgent': 'AI客服',
    'od.escalated': '已升级',
    'od.sendMessage': '发送消息',
    'od.testScenarios': '测试场景',
    'od.selectedScenario': '已选场景',
    'od.expected': '预期:',
    'od.conversation': '对话',
    'od.sessionLabel': '会话参考:',
    'od.languageLabel': '语言:',
    'od.intentLabel': '意图:',
    'od.detecting': '检测中...',
    'od.testEmailOverride': '测试邮箱(覆盖)',
    'od.optionalSimulatorOnly': '(可选 — 仅模拟器)',
    'od.emailOverridePlaceholder': '例如 you@example.com — 绕过电话查询',
    'od.customerName': '客户姓名',
    'od.escalatingTo': '正在升级至 {target}: {reason}',
    'od.processingFailed': '处理失败',
    'od.processFailedFallback': '无法处理您的消息。请重试。',
    'od.callEnded': '通话已结束',
    'od.tabBasic': '基础',
    'od.tabComplex': '复杂',
    'od.tabAmbiguous': '模糊',
    'od.tabMultilingual': '多语言',
    'od.tabEmpathy': '共情',
    'od.tabEnvironment': '环境',
    'od.tabContextual': '上下文',
    'od.subtitleUnifiedDesktop': '在一个界面中集成所有渠道、客户数据和工具',
    'od.takeBreak': '休息',
    'od.endBreak': '结束休息',
    'od.callsToday': '今日通话',
    'od.avgHandleTime': '平均处理时长',
    'od.target': '目标: {value}',
    'od.firstCallResolution': '首次通话解决率',
    'od.customerSatisfaction': '客户满意度',
    'od.ticketsResolved': '已解决工单',
    'od.performanceExcellent': '表现: 优秀',
    'od.channelOverview': '渠道概览',
    'od.channelVoice': '语音',
    'od.channelChat': '聊天',
    'od.channelEmail': '邮件',
    'od.channelSocial': '社交',
    'od.callControls': '通话控制',
    'od.answer': '接听',
    'od.incomingHandoff': '来电转接',
    'od.notEligible': '您无法接听此转接',
    'od.assignedOther': '已分配给其他坐席',
    'od.noLongerAvailable': '不再可用',
    'od.handoffExpired': '转接已过期',
    'od.alreadyAcceptedOther': '已被其他坐席接受',
    'od.handoffLocked': '此转接无法从您的工作台处理',
    'od.onHold': '保持中',
    'od.live': '进行中',
    'od.connectedNoAudio': '已连接 — 无实时音频',
    'od.handoffChannel': '{channel} 转接',
    'od.sourceAiVoice': 'AI 语音',
    'od.sourceAiChat': 'AI 聊天',
    'od.handoffToast': '客户转接来电',
    'od.handoffToastUrgent': '紧急客户转接',
    'od.opCallIncoming': '运营者来电',
    'od.opCallIncomingVideo': '视频来电',
    'od.opCallIncomingVoice': '语音来电',
    'od.opCallCustomer': '客户',
    'od.opCallDecline': '拒接',
    'od.opCallStarting': '正在开始…',
    'od.opCallAnswered': '已接听',
    'od.opCallConnecting': '正在连接 — 等待来电者音频',
    'od.opCallAnsweredElsewhere': '已在其他设备接听',
    'od.opCallAnsweredElsewhereDesc': '此通话已在其他设备接听。',
    'od.opCallFailed': '无法接听',
    'od.opCallNoConnection': '没有实时连接。请重新连接后再试。',
    'od.hangUp': '挂断',
    'od.resume': '恢复',
    'od.hold': '保持',
    'od.mute': '静音',
    'od.unmute': '取消静音',
    'od.volume': '音量',
    'od.customerInteraction': '客户交互 - {channel}',
    'od.liveCall': '实时通话',
    'od.issueLabel': '问题:',
    'od.prioritySuffix': '{priority} 优先级',
    'od.customerHistory': '客户历史',
    'od.resolved': '已解决',
    'od.pending': '待处理',
    'od.callNotes': '通话备注',
    'od.addNotesPlaceholder': '添加关于此通话的备注...',
    'od.quickResponsePlaceholder': '快速回复...',
    'od.handlingAs': '以 {name} 身份处理',
    'od.callQualityScore': '通话质量评分',
    'od.micMuted': '麦克风已静音',
    'od.saveResolve': '保存并解决',
    'od.escalate': '升级',
    'od.transfer': '转接',
    'od.readyNext': '准备好进行下一次交互',
    'od.waitingIncoming': '正在等待传入的 {channel} 交互',
    'od.queueWaiting': '队列: {count} 个等待中',
    'od.tabKB': '知识库',
    'od.tabQuick': '快捷',
    'od.tabTools': '工具',
    'od.knowledgeBase': '知识库',
    'od.searchSolutions': '搜索解决方案...',
    'od.noApprovedSolutions': '尚无已批准的解决方案。',
    'od.noSolutionsMatch': '没有与您的搜索匹配的解决方案。',
    'od.clickToInsert': '点击将此解决方案插入您的回复',
    'od.usesSuffix': '{count} 次使用',
    'od.quickResponses': '快速回复',
    'od.productivityTools': '生产力工具',
    'od.createTicket': '创建工单',
    'od.createNewTicket': '创建新工单',
    'od.createTicketDesc': '为当前客户问题创建支持工单',
    'od.titleRequired': '标题 *',
    'od.descriptionRequired': '描述 *',
    'od.priority': '优先级',
    'od.category': '类别',
    'od.customerNameEmailPlaceholder': '客户姓名或邮箱',
    'od.briefIssuePlaceholder': '问题的简要描述',
    'od.detailedIssuePlaceholder': '问题的详细描述',
    'od.priorityLow': '低',
    'od.priorityMedium': '中',
    'od.priorityHigh': '高',
    'od.priorityUrgent': '紧急',
    'od.catTechnical': '技术支持',
    'od.catBilling': '账单',
    'od.catProduct': '产品问题',
    'od.catAccount': '账户管理',
    'od.scheduleCallback': '安排回电',
    'od.scheduleCallbackDesc': '为客户安排回电',
    'od.customerNameRequired': '客户姓名 *',
    'od.phoneNumber': '电话号码',
    'od.customerNamePlaceholder': '客户姓名',
    'od.phonePlaceholder': '+1-555-0123',
    'od.dateRequired': '日期 *',
    'od.timeRequired': '时间 *',
    'od.notes': '备注',
    'od.callbackNotesPlaceholder': '回电的附加备注',
    'od.generateReport': '生成报告',
    'od.generateReportDesc': '生成绩效或活动报告',
    'od.reportType': '报告类型',
    'od.timePeriod': '时间段',
    'od.format': '格式',
    'od.reportPerformance': '绩效报告',
    'od.reportActivity': '活动报告',
    'od.reportCustomer': '客户交互',
    'od.reportResolution': '解决摘要',
    'od.periodToday': '今天',
    'od.periodWeek': '本周',
    'od.periodMonth': '本月',
    'od.periodQuarter': '本季度',
    'od.screenShare': '屏幕共享',
    'od.screenShareDesc': '与客户开始屏幕共享会话',
    'od.startScreenShare': '开始屏幕共享',
    'od.screenShareInfo': '这将允许客户查看您的屏幕以获得更好的协助',
    'od.secureConnection': '安全连接',
    'od.endToEndEncrypted': '端到端加密',
    'od.customerNotified': '将通知客户',
    'od.transferCall': '转接通话',
    'od.transferCallDesc': '选择将当前通话转接至何处',
    'od.transferTo': '转接至',
    'od.selectTransferDest': '选择转接目标',
    'od.destManager': '经理',
    'od.destBilling': '账单部门',
    'od.destSales': '销售团队',
    'od.destSupervisor': '主管',
    'od.adjustVolume': '调整通话音量',
    'od.adjustVolumeDesc': '调整当前通话的音量级别',
    'od.volumeLevel': '音量级别: {value}%',
    'od.saveResolveTicket': '保存并解决工单',
    'od.saveResolveDesc': '添加解决备注并将此工单标记为已解决',
    'od.resolutionNotesLabel': '解决备注 *',
    'od.resolutionPlaceholder': '描述问题是如何解决的...',
    'od.resolveInfo': '这将把工单标记为已解决并关闭当前交互。',
    'od.escalateTicket': '升级工单',
    'od.escalateTicketDesc': '将此工单升级给主管或专家',
    'od.escalationReasonLabel': '升级原因 *',
    'od.escalationPlaceholder': '说明为什么需要升级此工单...',
    'od.escalateTo': '升级至',
    'od.escAdmin': '管理员',
    'od.escalateInfo': '工单将转交给所选团队以获得进一步协助。',
    'od.noIncomingCall': '无来电',
    'od.noIncomingCallDesc': '没有可接听的来电。',
    'od.callOnHold': '通话保持中',
    'od.callOnHoldDesc': '麦克风已静音。客户处于保持状态。',
    'od.callResumed': '通话已恢复',
    'od.callResumedDesc': '麦克风已激活。客户可以听到您的声音。',
    'od.callDisconnected': '通话已断开。',
    'od.transferTargetRequired': '需要转接目标',
    'od.transferTargetRequiredDesc': '请选择要转接的员工。',
    'od.noActiveCall': '无活动通话',
    'od.noActiveCallTransfer': '没有可转接的活动 WebRTC 通话。',
    'od.callTransferred': '通话已转接',
    'od.callTransferredDesc': '通话已转接给员工 {target}。',
    'od.transferFailed': '转接失败',
    'od.noSolutionText': '无解决方案文本',
    'od.noSolutionTextDesc': '此解决方案尚无可插入的内容。',
    'od.solutionInserted': '已插入解决方案',
    'od.solutionInsertedDesc': '已将“{title}”添加到您的回复。',
    'od.solution': '解决方案',
    'od.fillTitleDescription': '请填写标题和描述',
    'od.ticketCreated': '工单已创建',
    'od.ticketCreatedDesc': '对话 {id} 已创建。',
    'od.createFailed': '创建失败',
    'od.fillCustomerDateTime': '请填写客户、日期和时间',
    'od.callbackScheduled': '回电已安排',
    'od.callbackScheduledDesc': '{customer} 的回电已保存。',
    'od.callbackFailed': '回电失败',
    'od.reportDownloaded': '报告已下载',
    'od.reportDownloadedDesc': '报告已根据实时数据生成。',
    'od.reportFailed': '报告失败',
    'od.screenShareRequiresCall': '屏幕共享需要与客户进行活动的 WebRTC 通话。',
    'od.screenShareActive': '屏幕共享已激活',
    'od.screenShareActiveDesc': '您的屏幕现在对客户可见。',
    'od.screenShareFailed': '屏幕共享失败',
    'od.screenShareFailedFallback': '无法开始屏幕共享。',
    'od.resolutionNotesRequired': '需要解决备注',
    'od.resolutionNotesRequiredDesc': '保存前请提供解决备注。',
    'od.noActiveTicket': '无活动工单',
    'od.selectTicketResolve': '解决前请选择一个工单。',
    'od.ticketResolved': '工单已解决',
    'od.ticketResolvedDesc': '工单 {id} 已解决并保存。',
    'od.resolutionFailed': '解决失败',
    'od.escalationReasonRequired': '需要升级原因',
    'od.escalationReasonRequiredDesc': '请提供升级原因。',
    'od.selectTicketEscalate': '升级前请选择一个工单。',
    'od.escalationCreatedDesc': '升级 {id} 已创建。',
    'od.escalationFailed': '升级失败',
    'od.noConversationLinked': '无关联对话',
    'od.noConversationLinkedDesc': '此升级没有关联的对话。无法发送消息。',
    'od.messageSent': '消息已发送',
    'od.messageSentDesc': '客户将收到此消息。',
    'od.sendFailed': '发送失败',
    'od.subtitleVoiceSimulator': '使用复杂场景、多语言查询和情感情境测试 AI 语音客服',
    'od.crmAuthReady': 'CRM 认证就绪',
    'od.calling': '正在呼叫:',
    'od.selectScenarioVoice': '选择一个场景以测试 AI 功能',
    'od.selectLanguage': '选择语言',
    'od.langEnglish': '英语',
    'od.langFrench': '法语',
    'od.langMixed': '混合',
    'od.initiateCall': '发起通话',
    'od.endCall': '结束通话',
    'od.processing': '处理中',
    'od.active': '活动',
    'od.idle': '空闲',
    'od.initiateCallToStart': '发起通话以开始测试',
    'od.listening': '正在聆听...',
    'od.clickToSpeak': '点击说一条消息',
    'od.turnOffVoiceMode': '关闭连续语音模式',
    'od.turnOnVoiceMode': '开启连续语音模式',
    'od.aiVoice': 'AI 语音',
    'od.on': '开',
    'od.off': '关',
    'od.thinking': '思考中',
    'od.voiceInputPlaceholder': '输入消息并按 Enter 键(Shift+Enter 换行),或使用上方的麦克风按钮...',
    'od.vsReady': '就绪',
    'od.vsProcessing': '处理中...',
    'od.vsSpeaking': '正在讲话...',
    'od.vsError': '麦克风错误',
    'od.notSupported': '不支持',
    'od.speechRequires': '语音识别需要 Chrome、Edge 或 Safari 14.1+。',
    'od.micBlocked': '麦克风被阻止',
    'od.micBlockedDesc': '请在浏览器设置中允许麦克风访问,然后重试。',
    'od.callInitiated': '通话已发起',
    'od.aiVoiceConnected': 'AI 语音客服已连接。会话: {session}',
    'od.callFailed': '通话失败',
    'od.callFailedDesc': '无法发起 AI 通话。请重试。',
    'od.callEscalated': '通话已升级',
    'od.aiVoiceTerminated': 'AI 语音会话已终止。',
    'od.chatSimSubtitle': 'QA 测试模拟器 — 将输入的消息直接发送到实时 AI 聊天后端。并非真实客户会话。使用 30 多个场景测试 AI 聊天客服,包括复杂情况、多语言查询和情感情境。',
    'od.qaSimulatorMode': 'QA 模拟器模式',
    'od.userLabel': '用户:',
    'od.guest': '访客',
    'od.crmAuth': 'CRM 认证',
    'od.restart': '重启',
    'od.connected': '已连接',
    'od.aiActive': 'AI 活动',
    'od.realTime': '实时',
    'od.voiceReady': '语音就绪',
    'od.listeningShort': '聆听中',
    'od.chattingWith': '正在与其聊天:',
    'od.selectScenarioChat': '选择一个场景以测试 AI 聊天功能',
    'od.chatControls': '聊天控制',
    'od.customerPhone': '客户电话',
    'od.startChat': '开始聊天',
    'od.endChat': '结束聊天',
    'od.startChatToTest': '开始聊天会话以开始测试',
    'od.chatInputPlaceholder': '输入您的消息或选择一个场景...',
    'od.clear': '清除',
    'od.chatInitiated': '聊天已开始',
    'od.aiChatConnected': 'AI 聊天客服已连接。会话: {session}',
    'od.chatFailed': '聊天失败',
    'od.chatFailedDesc': '无法开始 AI 聊天。请重试。',
    'od.chatEscalated': '聊天已升级',
    'od.chatEnded': '聊天已结束',
    'od.aiChatTerminated': 'AI 聊天会话已终止。',
  },
  ar: {
    'od.enableSound': 'تفعيل الصوت',
    // ── AI-Voice human handoff (Phase C): media state + transcript/summary ──
    'od.mediaJoining': 'جارٍ توصيلك بالعميل…',
    'od.waitingForCustomerOffer': 'في انتظار اتصال العميل…',
    'od.mediaConnecting': 'جارٍ إنشاء الصوت المباشر…',
    'od.humanCallActive': 'مكالمة بشرية نشطة — تم توصيل الصوت المباشر',
    'od.mediaFailed': 'تعذّر إنشاء الصوت المباشر. تم إخطار العميل.',
    'od.aiContext': 'سياق الذكاء الاصطناعي',
    'od.liveTranscript': 'النص المباشر',
    'od.callSummary': 'ملخص المكالمة',
    'od.actionItems': 'بنود الإجراءات',
    'od.commitments': 'الالتزامات',
    'od.speakerAgent': 'الوكيل',
    'od.speakerCustomer': 'العميل',
    'od.networkError': 'خطأ في الشبكة',
    'od.missingInformation': 'معلومات ناقصة',
    'od.customer': 'العميل',
    'od.aiAgent': 'وكيل الذكاء الاصطناعي',
    'od.escalated': 'تم التصعيد',
    'od.sendMessage': 'إرسال الرسالة',
    'od.testScenarios': 'سيناريوهات الاختبار',
    'od.selectedScenario': 'السيناريو المحدد',
    'od.expected': 'المتوقع:',
    'od.conversation': 'المحادثة',
    'od.sessionLabel': 'مرجع الجلسة:',
    'od.languageLabel': 'اللغة:',
    'od.intentLabel': 'النية:',
    'od.detecting': 'جارٍ الكشف...',
    'od.testEmailOverride': 'بريد إلكتروني للاختبار (تجاوز)',
    'od.optionalSimulatorOnly': '(اختياري — المحاكي فقط)',
    'od.emailOverridePlaceholder': 'مثال: you@example.com — يتجاوز البحث بالهاتف',
    'od.customerName': 'اسم العميل',
    'od.escalatingTo': 'التصعيد إلى {target}: {reason}',
    'od.processingFailed': 'فشلت المعالجة',
    'od.processFailedFallback': 'تعذّرت معالجة رسالتك. يرجى المحاولة مرة أخرى.',
    'od.callEnded': 'انتهت المكالمة',
    'od.tabBasic': 'أساسي',
    'od.tabComplex': 'معقد',
    'od.tabAmbiguous': 'غامض',
    'od.tabMultilingual': 'متعدد اللغات',
    'od.tabEmpathy': 'التعاطف',
    'od.tabEnvironment': 'البيئة',
    'od.tabContextual': 'سياقي',
    'od.subtitleUnifiedDesktop': 'جميع القنوات وبيانات العملاء والأدوات في واجهة واحدة',
    'od.takeBreak': 'أخذ استراحة',
    'od.endBreak': 'إنهاء الاستراحة',
    'od.callsToday': 'مكالمات اليوم',
    'od.avgHandleTime': 'متوسط وقت المعالجة',
    'od.target': 'الهدف: {value}',
    'od.firstCallResolution': 'الحل من أول مكالمة',
    'od.customerSatisfaction': 'رضا العملاء',
    'od.ticketsResolved': 'التذاكر المحلولة',
    'od.performanceExcellent': 'الأداء: ممتاز',
    'od.channelOverview': 'نظرة عامة على القنوات',
    'od.channelVoice': 'صوت',
    'od.channelChat': 'دردشة',
    'od.channelEmail': 'بريد إلكتروني',
    'od.channelSocial': 'وسائل التواصل',
    'od.callControls': 'عناصر التحكم في المكالمة',
    'od.answer': 'الرد',
    'od.incomingHandoff': 'تحويل وارد',
    'od.notEligible': 'لا يمكنك الرد على هذا التحويل',
    'od.assignedOther': 'تم تعيينه لمشغل آخر',
    'od.noLongerAvailable': 'لم يعد متاحًا',
    'od.handoffExpired': 'انتهت صلاحية التحويل',
    'od.alreadyAcceptedOther': 'تم قبوله بالفعل من قبل مشغل آخر',
    'od.handoffLocked': 'لا يمكن التعامل مع هذا التحويل من مكتبك',
    'od.onHold': 'قيد الانتظار',
    'od.live': 'مباشر',
    'od.connectedNoAudio': 'متصل — لا يوجد صوت مباشر',
    'od.handoffChannel': 'تحويل {channel}',
    'od.sourceAiVoice': 'صوت الذكاء الاصطناعي',
    'od.sourceAiChat': 'دردشة الذكاء الاصطناعي',
    'od.handoffToast': 'تحويل عميل وارد',
    'od.handoffToastUrgent': 'تحويل عميل عاجل',
    'od.opCallIncoming': 'مكالمة عامل واردة',
    'od.opCallIncomingVideo': 'مكالمة فيديو واردة',
    'od.opCallIncomingVoice': 'مكالمة صوتية واردة',
    'od.opCallCustomer': 'العميل',
    'od.opCallDecline': 'رفض',
    'od.opCallStarting': 'جارٍ البدء…',
    'od.opCallAnswered': 'تم الرد على المكالمة',
    'od.opCallConnecting': 'جارٍ الاتصال — في انتظار صوت المتصل',
    'od.opCallAnsweredElsewhere': 'تم الرد على جهاز آخر',
    'od.opCallAnsweredElsewhereDesc': 'تم الرد على هذه المكالمة على جهاز آخر.',
    'od.opCallFailed': 'تعذّر الرد',
    'od.opCallNoConnection': 'لا يوجد اتصال نشط. أعد الاتصال ثم حاول مرة أخرى.',
    'od.hangUp': 'إنهاء المكالمة',
    'od.resume': 'استئناف',
    'od.hold': 'تعليق',
    'od.mute': 'كتم الصوت',
    'od.unmute': 'إلغاء كتم الصوت',
    'od.volume': 'مستوى الصوت',
    'od.customerInteraction': 'تفاعل العميل - {channel}',
    'od.liveCall': 'مكالمة مباشرة',
    'od.issueLabel': 'المشكلة:',
    'od.prioritySuffix': 'أولوية {priority}',
    'od.customerHistory': 'سجل العميل',
    'od.resolved': 'تم الحل',
    'od.pending': 'قيد الانتظار',
    'od.callNotes': 'ملاحظات المكالمة',
    'od.addNotesPlaceholder': 'أضف ملاحظات حول هذه المكالمة...',
    'od.quickResponsePlaceholder': 'رد سريع...',
    'od.handlingAs': 'المعالجة بصفة {name}',
    'od.callQualityScore': 'درجة جودة المكالمة',
    'od.micMuted': 'الميكروفون مكتوم',
    'od.saveResolve': 'حفظ وحل',
    'od.escalate': 'تصعيد',
    'od.transfer': 'تحويل',
    'od.readyNext': 'جاهز للتفاعل التالي',
    'od.waitingIncoming': 'في انتظار تفاعل {channel} وارد',
    'od.queueWaiting': 'قائمة الانتظار: {count} في الانتظار',
    'od.tabKB': 'قاعدة المعرفة',
    'od.tabQuick': 'سريعة',
    'od.tabTools': 'أدوات',
    'od.knowledgeBase': 'قاعدة المعرفة',
    'od.searchSolutions': 'البحث عن الحلول...',
    'od.noApprovedSolutions': 'لا توجد حلول معتمدة بعد.',
    'od.noSolutionsMatch': 'لا توجد حلول تطابق بحثك.',
    'od.clickToInsert': 'انقر لإدراج هذا الحل في ردك',
    'od.usesSuffix': '{count} استخدام',
    'od.quickResponses': 'الردود السريعة',
    'od.productivityTools': 'أدوات الإنتاجية',
    'od.createTicket': 'إنشاء تذكرة',
    'od.createNewTicket': 'إنشاء تذكرة جديدة',
    'od.createTicketDesc': 'أنشئ تذكرة دعم لمشكلة العميل الحالية',
    'od.titleRequired': 'العنوان *',
    'od.descriptionRequired': 'الوصف *',
    'od.priority': 'الأولوية',
    'od.category': 'الفئة',
    'od.customerNameEmailPlaceholder': 'اسم العميل أو بريده الإلكتروني',
    'od.briefIssuePlaceholder': 'وصف موجز للمشكلة',
    'od.detailedIssuePlaceholder': 'وصف تفصيلي للمشكلة',
    'od.priorityLow': 'منخفضة',
    'od.priorityMedium': 'متوسطة',
    'od.priorityHigh': 'عالية',
    'od.priorityUrgent': 'عاجلة',
    'od.catTechnical': 'الدعم الفني',
    'od.catBilling': 'الفوترة',
    'od.catProduct': 'مشكلات المنتج',
    'od.catAccount': 'إدارة الحساب',
    'od.scheduleCallback': 'جدولة معاودة الاتصال',
    'od.scheduleCallbackDesc': 'جدولة معاودة اتصال للعميل',
    'od.customerNameRequired': 'اسم العميل *',
    'od.phoneNumber': 'رقم الهاتف',
    'od.customerNamePlaceholder': 'اسم العميل',
    'od.phonePlaceholder': '+1-555-0123',
    'od.dateRequired': 'التاريخ *',
    'od.timeRequired': 'الوقت *',
    'od.notes': 'ملاحظات',
    'od.callbackNotesPlaceholder': 'ملاحظات إضافية لمعاودة الاتصال',
    'od.generateReport': 'إنشاء تقرير',
    'od.generateReportDesc': 'أنشئ تقرير أداء أو نشاط',
    'od.reportType': 'نوع التقرير',
    'od.timePeriod': 'الفترة الزمنية',
    'od.format': 'التنسيق',
    'od.reportPerformance': 'تقرير الأداء',
    'od.reportActivity': 'تقرير النشاط',
    'od.reportCustomer': 'تفاعلات العملاء',
    'od.reportResolution': 'ملخص الحلول',
    'od.periodToday': 'اليوم',
    'od.periodWeek': 'هذا الأسبوع',
    'od.periodMonth': 'هذا الشهر',
    'od.periodQuarter': 'هذا الربع',
    'od.screenShare': 'مشاركة الشاشة',
    'od.screenShareDesc': 'ابدأ جلسة مشاركة شاشة مع العميل',
    'od.startScreenShare': 'بدء مشاركة الشاشة',
    'od.screenShareInfo': 'سيسمح هذا للعميل برؤية شاشتك للحصول على مساعدة أفضل',
    'od.secureConnection': 'اتصال آمن',
    'od.endToEndEncrypted': 'مشفّر من طرف إلى طرف',
    'od.customerNotified': 'سيتم إعلام العميل',
    'od.transferCall': 'تحويل المكالمة',
    'od.transferCallDesc': 'حدد وجهة تحويل المكالمة الحالية',
    'od.transferTo': 'تحويل إلى',
    'od.selectTransferDest': 'حدد وجهة التحويل',
    'od.destManager': 'مدير',
    'od.destBilling': 'قسم الفوترة',
    'od.destSales': 'فريق المبيعات',
    'od.destSupervisor': 'مشرف',
    'od.adjustVolume': 'ضبط مستوى صوت المكالمة',
    'od.adjustVolumeDesc': 'اضبط مستوى صوت المكالمة الحالية',
    'od.volumeLevel': 'مستوى الصوت: {value}%',
    'od.saveResolveTicket': 'حفظ وحل التذكرة',
    'od.saveResolveDesc': 'أضف ملاحظات الحل ووضع علامة على هذه التذكرة كمحلولة',
    'od.resolutionNotesLabel': 'ملاحظات الحل *',
    'od.resolutionPlaceholder': 'صف كيف تم حل المشكلة...',
    'od.resolveInfo': 'سيؤدي هذا إلى وضع علامة على التذكرة كمحلولة وإغلاق التفاعل الحالي.',
    'od.escalateTicket': 'تصعيد التذكرة',
    'od.escalateTicketDesc': 'صعّد هذه التذكرة إلى مشرف أو مختص',
    'od.escalationReasonLabel': 'سبب التصعيد *',
    'od.escalationPlaceholder': 'اشرح لماذا يجب تصعيد هذه التذكرة...',
    'od.escalateTo': 'تصعيد إلى',
    'od.escAdmin': 'مسؤول',
    'od.escalateInfo': 'سيتم تحويل التذكرة إلى الفريق المحدد للحصول على مزيد من المساعدة.',
    'od.noIncomingCall': 'لا توجد مكالمة واردة',
    'od.noIncomingCallDesc': 'لا توجد مكالمة واردة للرد عليها.',
    'od.callOnHold': 'المكالمة معلقة',
    'od.callOnHoldDesc': 'الميكروفون مكتوم. العميل في وضع الانتظار.',
    'od.callResumed': 'تم استئناف المكالمة',
    'od.callResumedDesc': 'الميكروفون نشط. يمكن للعميل سماعك.',
    'od.callDisconnected': 'تم قطع المكالمة.',
    'od.transferTargetRequired': 'وجهة التحويل مطلوبة',
    'od.transferTargetRequiredDesc': 'يرجى تحديد موظف للتحويل إليه.',
    'od.noActiveCall': 'لا توجد مكالمة نشطة',
    'od.noActiveCallTransfer': 'لا توجد مكالمة WebRTC نشطة للتحويل.',
    'od.callTransferred': 'تم تحويل المكالمة',
    'od.callTransferredDesc': 'تم تحويل المكالمة إلى الموظف {target}.',
    'od.transferFailed': 'فشل التحويل',
    'od.noSolutionText': 'لا يوجد نص للحل',
    'od.noSolutionTextDesc': 'هذا الحل ليس له محتوى قابل للإدراج بعد.',
    'od.solutionInserted': 'تم إدراج الحل',
    'od.solutionInsertedDesc': 'تمت إضافة "{title}" إلى ردك.',
    'od.solution': 'حل',
    'od.fillTitleDescription': 'يرجى ملء العنوان والوصف',
    'od.ticketCreated': 'تم إنشاء التذكرة',
    'od.ticketCreatedDesc': 'تم إنشاء المحادثة {id}.',
    'od.createFailed': 'فشل الإنشاء',
    'od.fillCustomerDateTime': 'يرجى ملء العميل والتاريخ والوقت',
    'od.callbackScheduled': 'تمت جدولة معاودة الاتصال',
    'od.callbackScheduledDesc': 'تم حفظ معاودة الاتصال لـ {customer}.',
    'od.callbackFailed': 'فشلت معاودة الاتصال',
    'od.reportDownloaded': 'تم تنزيل التقرير',
    'od.reportDownloadedDesc': 'تم إنشاء التقرير من بيانات مباشرة.',
    'od.reportFailed': 'فشل التقرير',
    'od.screenShareRequiresCall': 'تتطلب مشاركة الشاشة مكالمة WebRTC نشطة مع العميل.',
    'od.screenShareActive': 'مشاركة الشاشة نشطة',
    'od.screenShareActiveDesc': 'شاشتك مرئية الآن للعميل.',
    'od.screenShareFailed': 'فشلت مشاركة الشاشة',
    'od.screenShareFailedFallback': 'تعذّر بدء مشاركة الشاشة.',
    'od.resolutionNotesRequired': 'ملاحظات الحل مطلوبة',
    'od.resolutionNotesRequiredDesc': 'يرجى تقديم ملاحظات الحل قبل الحفظ.',
    'od.noActiveTicket': 'لا توجد تذكرة نشطة',
    'od.selectTicketResolve': 'حدد تذكرة قبل الحل.',
    'od.ticketResolved': 'تم حل التذكرة',
    'od.ticketResolvedDesc': 'تم حل التذكرة {id} وحفظها.',
    'od.resolutionFailed': 'فشل الحل',
    'od.escalationReasonRequired': 'سبب التصعيد مطلوب',
    'od.escalationReasonRequiredDesc': 'يرجى تقديم سبب للتصعيد.',
    'od.selectTicketEscalate': 'حدد تذكرة قبل التصعيد.',
    'od.escalationCreatedDesc': 'تم إنشاء التصعيد {id}.',
    'od.escalationFailed': 'فشل التصعيد',
    'od.noConversationLinked': 'لا توجد محادثة مرتبطة',
    'od.noConversationLinkedDesc': 'هذا التصعيد ليس له محادثة مرتبطة. لا يمكن إرسال الرسالة.',
    'od.messageSent': 'تم إرسال الرسالة',
    'od.messageSentDesc': 'سيتلقى العميل هذه الرسالة.',
    'od.sendFailed': 'فشل الإرسال',
    'od.subtitleVoiceSimulator': 'اختبر وكيل الصوت بالذكاء الاصطناعي بسيناريوهات معقدة واستعلامات متعددة اللغات وسياقات عاطفية',
    'od.crmAuthReady': 'مصادقة CRM جاهزة',
    'od.calling': 'جارٍ الاتصال:',
    'od.selectScenarioVoice': 'حدد سيناريو لاختبار قدرات الذكاء الاصطناعي',
    'od.selectLanguage': 'حدد اللغة',
    'od.langEnglish': 'الإنجليزية',
    'od.langFrench': 'الفرنسية',
    'od.langMixed': 'مختلط',
    'od.initiateCall': 'بدء المكالمة',
    'od.endCall': 'إنهاء المكالمة',
    'od.processing': 'جارٍ المعالجة',
    'od.active': 'نشط',
    'od.idle': 'خامل',
    'od.initiateCallToStart': 'ابدأ مكالمة لبدء الاختبار',
    'od.listening': 'جارٍ الاستماع...',
    'od.clickToSpeak': 'انقر للتحدث برسالة واحدة',
    'od.turnOffVoiceMode': 'إيقاف وضع الصوت المستمر',
    'od.turnOnVoiceMode': 'تشغيل وضع الصوت المستمر',
    'od.aiVoice': 'صوت الذكاء الاصطناعي',
    'od.on': 'تشغيل',
    'od.off': 'إيقاف',
    'od.thinking': 'جارٍ التفكير',
    'od.voiceInputPlaceholder': 'اكتب رسالة واضغط Enter (Shift+Enter لسطر جديد)، أو استخدم أزرار الميكروفون أعلاه...',
    'od.vsReady': 'جاهز',
    'od.vsProcessing': 'جارٍ المعالجة...',
    'od.vsSpeaking': 'جارٍ التحدث...',
    'od.vsError': 'خطأ في الميكروفون',
    'od.notSupported': 'غير مدعوم',
    'od.speechRequires': 'يتطلب التعرف على الكلام Chrome أو Edge أو Safari 14.1+.',
    'od.micBlocked': 'الميكروفون محظور',
    'od.micBlockedDesc': 'اسمح بالوصول إلى الميكروفون في إعدادات متصفحك وحاول مرة أخرى.',
    'od.callInitiated': 'تم بدء المكالمة',
    'od.aiVoiceConnected': 'تم الاتصال بوكيل الصوت بالذكاء الاصطناعي. الجلسة: {session}',
    'od.callFailed': 'فشلت المكالمة',
    'od.callFailedDesc': 'تعذّر بدء مكالمة الذكاء الاصطناعي. يرجى المحاولة مرة أخرى.',
    'od.callEscalated': 'تم تصعيد المكالمة',
    'od.aiVoiceTerminated': 'تم إنهاء جلسة الصوت بالذكاء الاصطناعي.',
    'od.chatSimSubtitle': 'محاكي اختبار ضمان الجودة — يرسل الرسائل المكتوبة مباشرة إلى الواجهة الخلفية لدردشة الذكاء الاصطناعي المباشرة. ليست جلسة عميل حقيقية. اختبر وكيل دردشة الذكاء الاصطناعي بأكثر من 30 سيناريو تشمل مواقف معقدة واستعلامات متعددة اللغات وسياقات عاطفية.',
    'od.qaSimulatorMode': 'وضع محاكي ضمان الجودة',
    'od.userLabel': 'المستخدم:',
    'od.guest': 'ضيف',
    'od.crmAuth': 'مصادقة CRM',
    'od.restart': 'إعادة التشغيل',
    'od.connected': 'متصل',
    'od.aiActive': 'الذكاء الاصطناعي نشط',
    'od.realTime': 'الوقت الفعلي',
    'od.voiceReady': 'الصوت جاهز',
    'od.listeningShort': 'جارٍ الاستماع',
    'od.chattingWith': 'الدردشة مع:',
    'od.selectScenarioChat': 'حدد سيناريو لاختبار قدرات دردشة الذكاء الاصطناعي',
    'od.chatControls': 'عناصر التحكم في الدردشة',
    'od.customerPhone': 'هاتف العميل',
    'od.startChat': 'بدء الدردشة',
    'od.endChat': 'إنهاء الدردشة',
    'od.startChatToTest': 'ابدأ جلسة دردشة لبدء الاختبار',
    'od.chatInputPlaceholder': 'اكتب رسالتك أو حدد سيناريو...',
    'od.clear': 'مسح',
    'od.chatInitiated': 'تم بدء الدردشة',
    'od.aiChatConnected': 'تم الاتصال بوكيل دردشة الذكاء الاصطناعي. الجلسة: {session}',
    'od.chatFailed': 'فشلت الدردشة',
    'od.chatFailedDesc': 'تعذّر بدء دردشة الذكاء الاصطناعي. يرجى المحاولة مرة أخرى.',
    'od.chatEscalated': 'تم تصعيد الدردشة',
    'od.chatEnded': 'انتهت الدردشة',
    'od.aiChatTerminated': 'تم إنهاء جلسة دردشة الذكاء الاصطناعي.',
  },
};

export default operatorDeskTranslations;
